import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  formatDate,
  formatCurrencyAmount,
  formatCurrencyAmountWhole,
  formatCurrency,
  parseIsoDateOnlyAsLocalDate,
} from "@utils/formatUtils";
import { CompliancePenaltyTooltipTitle } from "@components/Spaces/CompliancePenaltyTooltipTitle";
import { isTaxComplianceCalendarItem } from "@utils/penaltyCalendarTax";
import { differenceInDays, startOfDay } from "date-fns";
import ExcelJS from "exceljs";
import { useAffableConnect } from "@hooks/useAffableConnect";
import {
  getFacilityTypeSupplementLine,
  getHealthcareFacilityTypeTitle,
  getSelectedFacilityFromSession,
  mergeFacilityDetailsForTaxonomy,
} from "@utils/facilityUtils";
import {
  getSelectedCountryCode,
  isUnitedStatesCountryCode,
} from "@utils/countryUtils";
import AffableConnectedIcon from "./AffableConnectedIcon";
import { auditLogService } from "@services/auditLog";
import {
  getCurrentUserId,
  isGlobalAdministrator,
  userCanAccessHistoricPenalties,
} from "@utils/userUtils";
import {
  facilityComplianceService,
  type CyberExposureVector,
  type CybersecurityPenaltyExposureResponse,
  type MultiBodyAccreditationsResponse,
  type BodyProgramData,
} from "@services/facilityCompliance";
import { getAccreditingBody } from "@services/facilityManagement";
import { getHistoricPenaltiesAccordionSettings } from "@services/historicPenaltiesAccordionSettings";
import {
  shouldShowFacilityHistoricPenaltiesSection,
  type DashboardHistoricPenaltiesFacilitySummary,
} from "@services/facilityDashboard";
import { normalizeAccreditingBodySlug } from "@utils/facilityProfileAccrediting";
import { isCmsNursingHomePdcFacility, isCmsNursingHomePdcSlug } from "@utils/cmsNursingHomePdc";
import { CmsNursingHomePdcInfoTooltip } from "@components/Spaces/dashboard/dashboardInfoTooltips";
import { GlobalAdminLlmPromptButton } from "@components/Spaces/GlobalAdminLlmPromptButton";
import HistoricPenaltiesSection from "@components/Spaces/HistoricPenaltiesSection";
import SomSectionDetailDialog from "@components/Spaces/SomSectionDetailDialog";
import { stripMarkdownLight } from "@utils/stripMarkdownLight";

const StyledAccordion = styled(Accordion)({
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 0 8px 0",
  },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
  "& .MuiAccordionSummary-root": {
    alignItems: "center",
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    flexWrap: "nowrap",
  },
});
const ComplianceHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  gap: "8px",
  padding: "4px",
  background: "#fff",
  alignSelf: "stretch",
  width: "100%",
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Rethink Sans, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

/** One persisted enforcement action surfaced on a compliance calendar row (API camelCase). */
export type HistoricPenaltyEventDetail = {
  actionDate: string | null;
  amount: number;
  currency?: string;
  title: string;
  somTags: string[];
  violationCode?: string | null;
  reference?: string | null;
  caseNumber?: string | null;
  issuingAuthority: string;
  jurisdiction: string;
  source: string;
  sourceUrl?: string | null;
  regulator?: string | null;
  actionType?: string | null;
  caseType?: string | null;
  affectedIndividuals?: number | string | null;
  documentUrl?: string | null;
  description?: string;
  actionId?: number;
};

const HISTORIC_CODES_PREVIEW_COUNT = 20;

function historicCodesList(
  somTags: string[],
  violationCode?: string | null,
): string[] {
  if (somTags?.length) {
    return somTags.map((t) => t.trim()).filter(Boolean);
  }
  if (violationCode?.trim()) {
    return violationCode
      .split(/,\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Hide Source when it duplicates or is absorbed by the longer authority string. */
function isSourceRedundantWithAuthority(
  source: string,
  authority: string,
): boolean {
  const s = source.trim().toLowerCase();
  const a = authority.trim().toLowerCase();
  if (!s) return true;
  if (s === a) return true;
  if (a.includes(s)) return true;
  return false;
}

/** Turn "Penalty Type: Fine" into a shorter label + value for display. */
function parsePenaltyDetailDescription(description: string): {
  label: string;
  value: string;
} | null {
  const m = description.match(/^Penalty Type:\s*(.+)$/i);
  if (m) {
    return { label: "Penalty type", value: m[1].trim() };
  }
  return null;
}

function HistoricPenaltyCodeChips({
  somTags,
  violationCode,
}: {
  somTags: string[];
  violationCode?: string | null;
}) {
  const [showAll, setShowAll] = useState(false);
  const codes = useMemo(
    () => historicCodesList(somTags, violationCode),
    [somTags, violationCode],
  );

  if (codes.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: "14px",
          color: "#202124",
          fontFamily: "Rethink Sans, sans-serif",
        }}
      >
        —
      </Typography>
    );
  }

  const visible = showAll
    ? codes
    : codes.slice(0, HISTORIC_CODES_PREVIEW_COUNT);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          ...(showAll && codes.length > HISTORIC_CODES_PREVIEW_COUNT
            ? {
                maxHeight: 220,
                overflowY: "auto",
                pr: 0.5,
              }
            : {}),
        }}
      >
        {visible.map((code, index) => (
          <Chip
            key={`${code}-${index}`}
            label={code}
            size="small"
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: "12px",
              fontWeight: 500,
              height: 26,
              backgroundColor: "#e8f4f7",
              color: "#065b70",
              border: "1px solid #c5e4eb",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ))}
      </Box>
      {codes.length > HISTORIC_CODES_PREVIEW_COUNT ? (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setShowAll(!showAll)}
          sx={{
            mt: 1,
            display: "inline-block",
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "#065b70",
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
          }}
        >
          {showAll
            ? "Show less"
            : `Show all ${codes.length} codes`}
        </Link>
      ) : null}
    </Box>
  );
}

function complianceCalendarItemHasHistoricPenaltiesData(item: any): boolean {
  if (Array.isArray(item?.historicPenaltyEvents) && item.historicPenaltyEvents.length > 0) {
    return true;
  }
  return (
    Array.isArray(item?.historicPenaltiesByYear) &&
    item.historicPenaltiesByYear.length > 0
  );
}

function complianceCalendarItemHasSomTagsData(item: any): boolean {
  return Array.isArray(item?.somTags) && item.somTags.length > 0;
}

/**
 * Grid tracks that fill the row. Status (index 1) needs a floor so labels like
 * “Recommended” stay on one line (no mid-word breaks); other columns use minmax(0, …).
 */
function complianceCalendarGridTracks(...weights: number[]): string {
  return weights
    .map((w, index) =>
      index === 1
        ? `minmax(9rem, ${w}fr)`
        : `minmax(0, ${w}fr)`,
    )
    .join(" ");
}

interface PenaltyData {
  items?: Array<{
    name: string;
    ruleOrQuote: string;
    calculationNotes?: string;
    regulationCites?: Array<{ title: string; url: string }>;
    workedExample?: {
      result: {
        amount: number | string;
      };
      calculationNotes?: string;
    };
  }>;
  rollup?: {
    recurringAnnualCosts?: Array<{
      label: string;
      amount: {
        amount: number | string;
      };
    }>;
    contingentRisks?: Array<{
      text: string;
    }>;
  };
  complianceCalendar?: Array<{
    deadline: string;
    item: string;
    requirement: string;
    regulationCites?: Array<{ title: string; url: string }>;
    regulationCiteLinks?: Array<{ title: string; url: string }>;
    citation: {
      url: string;
      publisher?: string;
    };
    penaltyIfMissed: number | string;
    estimatedCostExposure: number | string;
    required?: boolean;
    somTags?: string[];
    surveyRiskSummary?: string | null;
    historicPenaltiesByYear?: Array<{
      year: number;
      amount?: number | null;
      amountText?: string | null;
      currency?: string;
    }>;
    historicPenaltyEvents?: HistoricPenaltyEventDetail[];
    surveyRiskScore?: number | null;
    surveyRiskReasons?: string[];
    /** When set to "ongoing", the row is a standing duty — not a specific filing deadline. */
    cadence?: string;
    /** "none" means no calendar due date; use with cadence for event-driven obligations. */
    deadlineType?: string;
  }>;
}

interface Facility {
  facility_id: number;
  facility_name: string;
  city: string;
  state: string;
  full_address: string;
  facility_type: string;
  facility_type_slug?: string;
  vertical?: string | null;
  country_code?: string | null;
  /** Objects from API / normalised saves; plain strings possible from legacy multiselect payloads. */
  accrediting_body?: ({ slug: string; name: string } | string)[] | null;
  /** Facility profile toggle — Tax Compliance Calendar accordion in Potential Penalties. */
  show_tax_compliance_calendar?: boolean;
}

export type RegulationCiteLink = { title: string; url: string };

function washingtonRegulationUrl(title: string): string | null {
  const rcw = title.match(/^RCW\s+(.+)$/i);
  if (rcw?.[1]) {
    return `https://app.leg.wa.gov/rcw/default.aspx?cite=${encodeURIComponent(
      rcw[1].trim(),
    )}`;
  }
  const wac = title.match(/^WAC\s+(.+)$/i);
  if (wac?.[1]) {
    return `https://app.leg.wa.gov/wac/default.aspx?cite=${encodeURIComponent(
      wac[1].trim(),
    )}`;
  }
  return null;
}

function parseTrailingWashingtonRegulationCites(
  text: string,
): { narrative: string; cites: RegulationCiteLink[] } {
  const source = text.trim();
  if (!source) return { narrative: "", cites: [] };
  const match = source.match(
    /^(.*?)(\b(?:RCW|WAC)\s+[^;]+(?:;\s*(?:RCW|WAC)\s+[^;]+)*\.?\s*)$/i,
  );
  if (!match?.[1] || !match[2]) {
    return { narrative: source, cites: [] };
  }
  const cites = match[2]
    .split(";")
    .map((raw) => raw.trim().replace(/\.\s*$/, ""))
    .map((title) => {
      const normalized = title.replace(/^(rcw|wac)\b/i, (prefix) =>
        prefix.toUpperCase(),
      );
      const url = washingtonRegulationUrl(normalized);
      return url ? { title: normalized, url } : null;
    })
    .filter((cite): cite is RegulationCiteLink => cite != null);
  if (!cites.length) return { narrative: source, cites: [] };
  return { narrative: match[1].trim(), cites };
}

/** DESCRIPTION cell: narrative plus linked source cites (regulationCites, WA parse, or citation.url). */
export function PenaltyRuleOrQuoteDescription({
  ruleOrQuote,
  regulationCites,
  citation,
}: {
  ruleOrQuote?: string;
  regulationCites?: RegulationCiteLink[];
  citation?: { title?: string | null; url?: string | null } | null;
}) {
  const parsed = parseTrailingWashingtonRegulationCites(ruleOrQuote ?? "");
  const suppliedCites =
    regulationCites?.filter((cite) => cite?.title && cite?.url) ?? [];
  const citationUrl =
    typeof citation?.url === "string" ? citation.url.trim() : "";
  const citationFallback: RegulationCiteLink[] =
    !suppliedCites.length && !parsed.cites.length && citationUrl
      ? [
          {
            title:
              (typeof citation?.title === "string" && citation.title.trim()) ||
              "Source",
            url: citationUrl,
          },
        ]
      : [];
  const cites = suppliedCites.length
    ? suppliedCites
    : parsed.cites.length
      ? parsed.cites
      : citationFallback;
  const displayRuleOrQuote =
    cites.length && parsed.cites.length ? parsed.narrative : (ruleOrQuote ?? "");
  if (!cites.length) {
    return <>{ruleOrQuote ?? ""}</>;
  }
  return (
    <>
      {displayRuleOrQuote ? (
        <Box component="span" sx={{ display: "block" }}>
          {displayRuleOrQuote}
        </Box>
      ) : null}
      {cites.map((cite, index) => (
        <Box
          key={`${cite.title}-${index}`}
          component="span"
          sx={{ display: "block", mt: displayRuleOrQuote ? 0.5 : 0 }}
        >
          <a
            href={cite.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0a829d", textDecoration: "none" }}
          >
            {cite.title}
          </a>
        </Box>
      ))}
    </>
  );
}

/** Quarter range for dynamic calendar tabs (exactly 2 quarters from asOfDate) */
export interface QuarterOption {
  label: string;
  id: string;
  start: string;
  end: string;
}

/** Return calendar quarters starting from the quarter that contains asOfDate (YYYY-MM-DD).
 *  Parses the date string directly (no Date constructor) to avoid UTC→local timezone shifts
 *  that would move a date like "2026-04-01" to March 31 in US Eastern time zones. */
export function getQuartersFromDate(
  asOfDateStr: string,
  quarterCount = 2,
): QuarterOption[] {
  const parts = asOfDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!parts) return [];
  const year = parseInt(parts[1], 10);
  const month = parseInt(parts[2], 10); // 1-12
  const q = Math.ceil(month / 3); // 1-4
  const quarters: QuarterOption[] = [];
  for (let i = 0; i < Math.max(0, quarterCount); i++) {
    const qNum = q + i <= 4 ? q + i : q + i - 4;
    const y = q + i <= 4 ? year : year + 1;
    const startMonth = (qNum - 1) * 3 + 1;
    const endMonth = qNum * 3;
    const start = `${y}-${String(startMonth).padStart(2, "0")}-01`;
    const endLastDay = new Date(y, endMonth, 0).getDate();
    const end = `${y}-${String(endMonth).padStart(2, "0")}-${String(endLastDay).padStart(2, "0")}`;
    quarters.push({
      label: `Q${qNum} ${y}`,
      id: `Q${qNum}-${y}`,
      start,
      end,
    });
  }
  return quarters;
}

/** Compliance API deadlines are often ISO date-only (YYYY-MM-DD). Parse as local calendar day for
 *  display, overdue, and day-count (same semantics as quarter filtering — not UTC midnight). */
function complianceDeadlineToDate(deadline: string): Date {
  return parseIsoDateOnlyAsLocalDate(deadline) ?? new Date(deadline);
}

/** True if a calendar item has no fixed due date (recurring/ongoing standing duty). */
export function isOngoingCalendarItem(
  calendarItem?: { cadence?: string; deadlineType?: string },
): boolean {
  return (
    calendarItem?.cadence === "ongoing" ||
    calendarItem?.deadlineType === "none"
  );
}

/** True if deadline falls within quarter start/end (inclusive).
 *  Ongoing/standing-duty items are NOT considered to belong to any quarter — they
 *  are surfaced in the dedicated "Upcoming" tab to avoid being shown on every quarter.
 *  Parses the date string directly (no Date constructor) to avoid UTC→local timezone shifts. */
export function isDateInQuarter(
  deadlineStr: string,
  quarter: { start: string; end: string },
  calendarItem?: { cadence?: string; deadlineType?: string },
): boolean {
  if (isOngoingCalendarItem(calendarItem)) return false;
  if (!deadlineStr || deadlineStr === "TBD") return false;
  const parts = deadlineStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!parts) return false;
  const dateStr = `${parts[1]}-${parts[2]}-${parts[3]}`;
  return dateStr >= quarter.start && dateStr <= quarter.end;
}

/** Build eCFR URL for a CFR reference (e.g. "42 CFR 488.430" -> eCFR section link). */
function buildCfrLink(cfrRef: string | null | undefined): string | null {
  if (!cfrRef || typeof cfrRef !== "string") return null;
  const m = cfrRef.match(/^(\d+)\s*CFR\s*([\d.]+)$/i);
  if (!m) return null;
  const title = m[1];
  const partSection = m[2];
  const hasSection = partSection.includes(".");
  const partNum = partSection.split(".")[0] || partSection;
  const base = `https://www.ecfr.gov/current/title-${title}/part-${partNum}`;
  return hasSection ? `${base}/section-${partSection}` : base;
}

interface PenaltyProps {
  penaltyData: PenaltyData | null;
  loading?: boolean;
  /** True while penalties are loading in the background after Facility Analysis (5-section) loaded. */
  penaltiesLoading?: boolean;
  selectedFacility?: Facility | null;
  /** Effective as_of_date for calendar (e.g. from entityDetails.asOfDate or dev override). Drives which 2 quarters are shown. */
  asOfDate?: string;
  /**
   * Global admins: exact LLM prompt for the penalties section (shared with compliance calendar).
   * Shown on the facility compliance calendar accordion only (when non-tax rows exist).
   */
  penaltiesLlmPrompt?: string;
  /** Global admins: wall-clock seconds for the penalties section LLM `ainvoke`. */
  penaltiesLlmWallSec?: number | null;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
  /** True when the Penalty tab is visible; defers accrediting-body API until first activation. */
  penaltyTabActive?: boolean;
  /**
   * Dashboard BFF facility historic-penalties summary. Used to show the Historic Penalties
   * accordion for non–CMS-NH-PDC facilities when the dashboard already has year data (#10126).
   */
  historicPenaltiesFacility?: Partial<DashboardHistoricPenaltiesFacilitySummary> | null;
}

// PenaltyIcon component
const PenaltyIcon = () => (
  <Box sx={{ width: "20px", height: "20px" }}>
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
        stroke="#065b70"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 6V10"
        stroke="#065b70"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 14H10.01"
        stroke="#065b70"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const PenaltyContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const MainContent = styled(Box)({
  display: "flex",
  flexDirection: "row",
  gap: "24px",
  width: "100%",
  alignItems: "flex-start",
});

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$hasActiveReport",
})<{ $hasActiveReport?: boolean }>(({ $hasActiveReport }) => ({
  display: "flex",
  flexDirection: "column",
  flex: "1",
  minWidth: "0",
  width: $hasActiveReport ? "50%" : "100%",
  transition: "width 0.3s ease-in-out",
}));

const CitationPanelContainer = styled(Box)({
  width: "50%",
  flexShrink: 0,
  position: "sticky",
  top: "16px",
  alignSelf: "stretch",
  overflow: "hidden",
  borderRadius: "8px",
  border: "1px solid #9EE5F4",
  backgroundColor: "#ffffff",
  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
  // height: "fit-content",
  // maxHeight: "calc(100vh - 32px)",
});

const CitationHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  backgroundColor: "#0a829d",
  borderRadius: "8px 8px 0 0",
});

const CloseButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: "0",
  borderRadius: "4px",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "white",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
});

// ---------------------------------------------------------------------------
// Penalties tab-table helpers
// ---------------------------------------------------------------------------

const penaltyTableHeaderSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#FFF",
  fontFamily: "Rethink Sans, sans-serif",
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: "1px solid #0a6a80",
  padding: "10px 12px",
  backgroundColor: "#0a829d",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

const penaltyTableCellSx = {
  fontSize: "13px",
  fontFamily: "Rethink Sans, sans-serif",
  color: "#262626",
  borderBottom: "1px solid #f0f0f0",
  padding: "10px 12px",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

const KEEP_HARD_NOTE_RE =
  /denial of payment|termination|405\.2404|405\.241[0-9]|Termination of rural health clinic agreements|terminat(?:e|ion) (?:the )?rural health clinic/i;

function penaltyItemNotes(item: any): string {
  const fromWorked = String(item?.workedExample?.calculationNotes ?? "").trim();
  const fromItem = String(item?.calculationNotes ?? "").trim();
  if (fromWorked && KEEP_HARD_NOTE_RE.test(fromWorked)) return fromWorked;
  if (fromItem && KEEP_HARD_NOTE_RE.test(fromItem)) return fromItem;
  return fromWorked || fromItem;
}

const RHC_ENFORCEMENT_BANNER =
  "RHC enforcement is denial of payment and/or termination of the rural health clinic agreement (42 CFR \u00a7 405.2404), not a fabricated dollar CMP.";

/** True when a note is the shared RHC 405.2404 enforcement blurb (not item-specific). */
function isSharedRhcEnforcementNote(note: string): boolean {
  const n = String(note ?? "").trim();
  if (!n) return false;
  if (n === RHC_ENFORCEMENT_BANNER) return true;
  return KEEP_HARD_NOTE_RE.test(n) && /405\.2404/i.test(n) && /denial of payment/i.test(n);
}

function rhcBannerHaystack(facility: Record<string, unknown> | null | undefined): string {
  if (!facility || typeof facility !== "object") return "";
  const merged = mergeFacilityDetailsForTaxonomy(facility);
  const row = merged as Record<string, unknown>;
  return [
    merged.facility_type_slug,
    merged.facility_type,
    merged.facility_name,
    row.name,
    merged.primary_taxonomy_code,
    merged.primary_taxonomy_desc,
    row.taxonomy_code,
    row.nucc_code,
    getHealthcareFacilityTypeTitle(merged),
    getFacilityTypeSupplementLine(merged),
  ]
    .map((v) => String(v ?? "").toLowerCase())
    .join(" ");
}

function showRhcEnforcementBanner(
  facility: {
    facility_type_slug?: string;
    facility_type?: string;
    facility_name?: string;
    name?: string;
  } | null | undefined,
  penaltyData: any,
): boolean {
  // Header chip sees 261QR1300X via mergeFacilityDetailsForTaxonomy; the raw
  // prop often does not (Mackay after profile work). Also consult session.
  const session = getSelectedFacilityFromSession<Record<string, unknown>>();
  const hay = [
    rhcBannerHaystack(facility as Record<string, unknown> | null | undefined),
    rhcBannerHaystack(session),
  ].join(" ");
  const rhcFqhc = /rural[\s_-]*health|\brhc\b|\bfqhc\b|federally[\s_-]*qualified|261qr1300x/.test(hay);
  const fromPayload =
    String(penaltyData?.enforcementRegime || "").toLowerCase().includes("denial")
    || String(penaltyData?.rollup?.assumptions?.enforcementRegime || "").toLowerCase().includes("denial")
    || ((penaltyData?.items as any[]) || []).some((it) => {
      const blob = [
        it?.enforcementRegime,
        it?.calculationNotes,
        it?.workedExample?.calculationNotes,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return blob.includes("denial") || blob.includes("405.2404");
    });
  return rhcFqhc || fromPayload;
}

/** Returns true when a penalty item recurs (per-day / daily / until corrected).
 *  Ongoing/standing-duty calendar items synthesised into the penalties table are
 *  treated as recurring by definition (they apply continuously until corrected). */
function detectRecurring(item: any): boolean {
  if (item?.cadence === "ongoing") return true;
  const notes = (item?.workedExample?.calculationNotes ?? "").toLowerCase();
  const formula = (item?.formula ?? "").toLowerCase();
  const rule = (item?.ruleOrQuote ?? "").toLowerCase();
  const combined = `${notes} ${formula} ${rule}`;
  if (/\bnot recurring\b|one.time|single.time/.test(combined)) return false;
  return /\brecurring\b|per.day|per day|daily|per.violation|accruing|until corrected|continuing/.test(
    combined,
  );
}

/** Parses minimal structured info from a contingent risk's free-text field. */
function extractContingentRiskInfo(risk: any): {
  name: string;
  trigger: string;
  exposure: string;
  riskLevel: "High" | "Medium" | "Low" | null;
} {
  const text: string = risk?.text ?? "";
  const dashIdx = text.indexOf(" — ");
  const dotIdx = text.indexOf(". ");
  let name = text;
  let trigger = text;
  if (dashIdx > 0) {
    name = text.slice(0, dashIdx).trim();
    trigger = text.slice(dashIdx + 3).trim();
  } else if (dotIdx > 0 && dotIdx < 80) {
    name = text.slice(0, dotIdx).trim();
    trigger = text.trim();
  }

  const expMatch =
    text.match(/up to \$([\d,]+(?:\.\d+)?)/i) ??
    text.match(/\$([\d,]+(?:\.\d+)?)(?:\s*per\s*(\w+))?/i);
  let exposure = "—";
  if (expMatch) {
    const raw = expMatch[1].replace(/,/g, "");
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const perMatch = text.match(/per\s+(\w+)/i);
      const unit = perMatch ? perMatch[1].toLowerCase() : null;
      exposure = `≤ ${formatCurrencyAmount(num)}${unit ? ` /${unit}` : ""}`;
    }
  }

  const lower = text.toLowerCase();
  let riskLevel: "High" | "Medium" | "Low" | null = null;
  if (/\bhigh\b/.test(lower)) riskLevel = "High";
  else if (/\bmedium\b/.test(lower)) riskLevel = "Medium";
  else if (/\blow\b/.test(lower)) riskLevel = "Low";
  else if (/\$10[,.]?000,000|\$10m|\$10 million/.test(lower)) riskLevel = "High";
  else if (/per day|daily|accruing/.test(lower)) riskLevel = "Medium";

  return { name, trigger, exposure, riskLevel };
}

const Penalty: React.FC<PenaltyProps> = ({
  penaltyData,
  loading,
  penaltiesLoading = false,
  selectedFacility,
  asOfDate: asOfDateProp,
  penaltiesLlmPrompt,
  penaltiesLlmWallSec,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = true,
  penaltyTabActive = false,
  historicPenaltiesFacility = null,
}) => {
  const showPenaltiesLlmOnCalendarAccordion = Boolean(
    penaltiesLlmPrompt?.trim() &&
      (penaltyData?.complianceCalendar ?? []).some(
        (item) => !isTaxComplianceCalendarItem(item),
      ),
  );
  const showPenaltiesLlmOnMainAccordion = Boolean(
    penaltiesLlmPrompt?.trim() && !showPenaltiesLlmOnCalendarAccordion,
  );
  const [penaltyTabEverActive, setPenaltyTabEverActive] = useState(penaltyTabActive);
  useEffect(() => {
    if (penaltyTabActive) {
      setPenaltyTabEverActive(true);
    }
  }, [penaltyTabActive]);

  const [expanded, setExpanded] = useState<string | false>(false);
  const [penaltySubTab, setPenaltySubTab] = useState<
    "penalties" | "recurring" | "contingent" | "accreditations"
  >("penalties");
  const [accreditationsData, setAccreditationsData] =
    useState<MultiBodyAccreditationsResponse | null>(null);
  const [accreditationsLoading, setAccreditationsLoading] = useState(false);
  const [accreditationsError, setAccreditationsError] = useState<string | null>(null);
  // DB-fresh accrediting body data fetched when the Accreditations sub-tab opens.
  // Takes precedence over selectedFacility.accrediting_body so the tab always
  // reflects the latest DB value even when the profile setup dialog is still open.
  const [freshAccreditingBodySlugs, setFreshAccreditingBodySlugs] = useState<string[] | null>(null);
  // Tracks the facility_id for which an accrediting-body fetch was last initiated.
  // Used instead of a `cancelled` flag so React 18 StrictMode double-invocation
  // doesn't cancel the in-flight fetch before it can update state.
  const accreditingBodyFetchFacilityRef = React.useRef<number | null>(null);
  /** Previous render's value of (expanded === "penaltiesAndTotalExposure"). */
  const prevPenaltiesAccordionExpandedRef = React.useRef(false);
  const prevPenaltySubTabRef = React.useRef(penaltySubTab);
  /** Detects Facility Profile updates while this panel stays mounted (hidden via display:none). */
  const prevFacilityAccreditingKeyRef = React.useRef<string>("");
  const [selectedCitation, setSelectedCitation] = useState<{
    url: string;
    requirement: string;
    publisher?: string;
  } | null>(null);
  const [somSectionDialogOpen, setSomSectionDialogOpen] = useState(false);
  const [somSectionDialogTag, setSomSectionDialogTag] = useState<string | null>(
    null,
  );
  const [historicPenaltyDialogEvent, setHistoricPenaltyDialogEvent] =
    useState<HistoricPenaltyEventDetail | null>(null);

  // Cybersecurity Compliance Risk accordion: fetch automatically and only show when coverage exists.
  const [cyberExposure, setCyberExposure] =
    useState<CybersecurityPenaltyExposureResponse | null>(null);
  const [cyberLoading, setCyberLoading] = useState(false);
  const [cyberError, setCyberError] = useState<string | null>(null);
  const cyberLoadingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [cyberRefreshNonce, setCyberRefreshNonce] = useState(0);
  const [cyberLoadingHint, setCyberLoadingHint] = useState(false);
  const [cyberViolations, setCyberViolations] = useState(1);
  const [cyberDays, setCyberDays] = useState(10);
  /** Applied when user clicks Calculate; used for Projected column */
  const [cyberAppliedViolations, setCyberAppliedViolations] = useState(1);
  const [cyberAppliedDays, setCyberAppliedDays] = useState(10);
  const [cyberRefDate, setCyberRefDate] = useState<string>(() => {
    if (asOfDateProp) return asOfDateProp;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  /** Row indices whose Projected value changed on last Calculate; highlight red for 5s */
  const [cyberHighlightedRows, setCyberHighlightedRows] = useState<Set<number>>(new Set());
  const cyberHighlightTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (cyberHighlightTimeoutRef.current) {
        clearTimeout(cyberHighlightTimeoutRef.current);
        cyberHighlightTimeoutRef.current = null;
      }
    };
  }, []);

  // Derive effective as_of_date: prop, or earliest deadline in calendar, or today.
  // Date strings are compared directly as "YYYY-MM-DD" to avoid UTC→local timezone shifts
  // (new Date("2026-04-01") resolves to March 31 in US Eastern time zones).
  const effectiveAsOfDate = useMemo(() => {
    if (asOfDateProp) return asOfDateProp;
    const calendar = penaltyData?.complianceCalendar ?? [];
    if (calendar.length === 0) return new Date().toISOString().split("T")[0];
    let minDateStr: string | null = null;
    calendar.forEach((item: { deadline?: string }) => {
      if (item.deadline && item.deadline !== "TBD") {
        const parts = item.deadline.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (parts) {
          const normalized = `${parts[1]}-${parts[2]}-${parts[3]}`;
          if (!minDateStr || normalized < minDateStr) minDateStr = normalized;
        }
      }
    });
    return minDateStr ?? new Date().toISOString().split("T")[0];
  }, [asOfDateProp, penaltyData?.complianceCalendar]);

  const complianceDeadlineQuarterCount = isGlobalAdministrator() ? 4 : 2;
  const quarterConfig = useMemo(
    () =>
      getQuartersFromDate(
        effectiveAsOfDate,
        complianceDeadlineQuarterCount,
      ),
    [effectiveAsOfDate, complianceDeadlineQuarterCount]
  );

  const [selectedQuarter, setSelectedQuarter] = useState<string>(() =>
    quarterConfig.length > 0 ? quarterConfig[0].label : ""
  );

  /** Rules /controls Features tab — default on; set false when server hides the accordion. */
  const [historicPenaltiesAccordionControlsEnabled, setHistoricPenaltiesAccordionControlsEnabled] =
    useState(true);

  useEffect(() => {
    let cancelled = false;
    getHistoricPenaltiesAccordionSettings().then((s) => {
      if (!cancelled) {
        setHistoricPenaltiesAccordionControlsEnabled(s.enabled);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep selectedQuarter in sync when quarterConfig changes (e.g. new asOfDate)
  // "Upcoming" is static and should not be reset
  useEffect(() => {
    if (
      quarterConfig.length > 0 &&
      selectedQuarter !== "Upcoming" &&
      !quarterConfig.some((q) => q.label === selectedQuarter)
    ) {
      setSelectedQuarter(quarterConfig[0].label);
    }
  }, [quarterConfig, selectedQuarter]);

  // Resolve facility type for cyber API (must match rules server canonical slugs, e.g. skilled_nursing_facility not "Skilled Nursing Facility (SNF)")
  const cyberFacilityType = useMemo(() => {
    const slug = (selectedFacility as { facility_type_slug?: string } | null)?.facility_type_slug?.trim();
    if (slug) return slug.toLowerCase().replace(/-/g, "_");
    const raw = selectedFacility?.facility_type ?? "";
    let normalized = raw
      .toLowerCase()
      .trim()
      .replace(/-/g, "_")
      .replace(/\s+/g, "_");
    // Strip parenthetical suffix so "skilled_nursing_facility_(snf)" -> "skilled_nursing_facility" (rules server expects this)
    const paren = normalized.indexOf("_(");
    if (paren > 0) normalized = normalized.slice(0, paren);
    return normalized || "";
  }, [selectedFacility]);
  const isCmsNursingHomePdc = useMemo(
    () =>
      isCmsNursingHomePdcFacility(selectedFacility) ||
      isCmsNursingHomePdcSlug(cyberFacilityType),
    [selectedFacility, cyberFacilityType],
  );
  const cyberCountryCode = useMemo(() => {
    const f = selectedFacility as { country_code?: string; country?: string } | null | undefined;
    return f?.country_code ?? f?.country ?? getSelectedCountryCode() ?? "USA";
  }, [selectedFacility]);
  const isUsaForHistoricPenalties = isUnitedStatesCountryCode(cyberCountryCode);

  const selectedQuarterOption = quarterConfig.find(
    (q) => q.label === selectedQuarter,
  );

  const isUpcomingSelected = selectedQuarter === "Upcoming";

  const facilityForVertical =
    selectedFacility ?? getSelectedFacilityFromSession<Facility>();
  const showTaxComplianceCalendar =
    facilityForVertical?.show_tax_compliance_calendar === true;
  const facilityVertical = String(facilityForVertical?.vertical ?? "")
    .trim()
    .toLowerCase();
  const facilityTypeSlugForVertical = String(
    facilityForVertical?.facility_type_slug ?? "",
  )
    .trim()
    .toLowerCase();
  const isHealthcareVertical = facilityVertical
    ? facilityVertical === "healthcare" || facilityVertical === "health_care"
    : !(
        facilityTypeSlugForVertical.startsWith("food_service_") ||
        facilityTypeSlugForVertical.startsWith("aviation_")
      );

  type ComplianceCalendarAccordionVm = {
    accordionKey: "complianceCalendar" | "taxComplianceCalendar";
    title: string;
    items: any[];
    filtered: any[];
    showSomTagsColumn: boolean;
    showHistoricPenaltiesColumn: boolean;
    complianceCalendarGridTemplateColumns: string;
    showLlmButton: boolean;
  };

  // TITLE26 / tax rows: facility accordion = non-tax only; tax accordion only when profile toggle ON.
  const complianceCalendarAccordions = useMemo((): ComplianceCalendarAccordionVm[] => {
    const fullComplianceCalendar = penaltyData?.complianceCalendar ?? [];
    if (fullComplianceCalendar.length === 0) return [];
    const facilityItems = fullComplianceCalendar.filter(
      (i) => !isTaxComplianceCalendarItem(i) && !isOngoingCalendarItem(i),
    );
    const hasNonTaxCalendarRows = fullComplianceCalendar.some(
      (i) => !isTaxComplianceCalendarItem(i),
    );
    const taxItems = fullComplianceCalendar.filter((i) =>
      isTaxComplianceCalendarItem(i),
    );

    const build = (
      accordionKey: ComplianceCalendarAccordionVm["accordionKey"],
      title: string,
      items: any[],
      showLlmButton: boolean,
      allowEmpty = false,
    ): ComplianceCalendarAccordionVm | null => {
      if (items.length === 0 && !allowEmpty) return null;
      const showSomTagsColumn = items.some(complianceCalendarItemHasSomTagsData);
      const showHistoricPenaltiesColumn =
        isHealthcareVertical &&
        isUsaForHistoricPenalties &&
        items.some(complianceCalendarItemHasHistoricPenaltiesData);
      const s = showSomTagsColumn;
      const h = showHistoricPenaltiesColumn;
      const complianceCalendarGridTemplateColumns =
        s && h
          ? complianceCalendarGridTracks(8, 8, 12, 13, 9, 10, 10, 9, 10)
          : s
            ? complianceCalendarGridTracks(8, 8, 13, 14, 10, 11, 11, 10)
            : h
              ? complianceCalendarGridTracks(8, 8, 14, 14, 10, 11, 12, 11)
              : complianceCalendarGridTracks(9, 8, 15, 15, 10, 12, 13);
      const filtered = items.filter((item: any) =>
        selectedQuarterOption
          ? isDateInQuarter(item.deadline, selectedQuarterOption, item)
          : false,
      );
      return {
        accordionKey,
        title,
        items,
        filtered,
        showSomTagsColumn,
        showHistoricPenaltiesColumn,
        complianceCalendarGridTemplateColumns,
        showLlmButton,
      };
    };

    const out: (ComplianceCalendarAccordionVm | null)[] = [
      build(
        "complianceCalendar",
        "Facility Compliance Calendar",
        facilityItems,
        true,
        hasNonTaxCalendarRows,
      ),
      ...(showTaxComplianceCalendar
        ? [
            build(
              "taxComplianceCalendar",
              "Tax Compliance Calendar",
              taxItems,
              false,
              true,
            ),
          ]
        : []),
    ];
    return out.filter(Boolean) as ComplianceCalendarAccordionVm[];
  }, [
    penaltyData?.complianceCalendar,
    selectedQuarterOption,
    isHealthcareVertical,
    isUsaForHistoricPenalties,
    showTaxComplianceCalendar,
  ]);

  useEffect(() => {
    if (!showTaxComplianceCalendar && expanded === "taxComplianceCalendar") {
      setExpanded(false);
    }
  }, [expanded, showTaxComplianceCalendar]);

  /** Surface ongoing/standing-duty calendar items (no fixed deadline) inside the
   *  Potential Penalties table — they live as "continuous obligations" rather than
   *  in any quarter tab. Tax-calendar items are excluded (they live in their own surface). */
  const ongoingPenaltyItemsFromCalendar = useMemo(() => {
    const cal = penaltyData?.complianceCalendar ?? [];
    return cal
      .filter((item) => isOngoingCalendarItem(item))
      .filter((item) => !isTaxComplianceCalendarItem(item))
      .map((item) => {
        const exposure =
          typeof item.estimatedCostExposure === "string"
            ? item.estimatedCostExposure.trim()
            : "";
        const penalty =
          typeof item.penaltyIfMissed === "string"
            ? item.penaltyIfMissed.trim()
            : "";
        const calculationNotes = exposure || penalty || "";
        return {
          name: item.item,
          ruleOrQuote: item.requirement,
          regulationCites: item.regulationCites ?? item.regulationCiteLinks,
          cadence: "ongoing",
          citation: item.citation,
          workedExample: {
            result: { amount: 0 },
            calculationNotes,
          },
        };
      });
  }, [penaltyData?.complianceCalendar]);

  const potentialPenaltyItems = useMemo(
    () => [
      ...((penaltyData?.items as any[]) ?? []),
      ...ongoingPenaltyItemsFromCalendar,
    ],
    [penaltyData?.items, ongoingPenaltyItemsFromCalendar],
  );

  const visibleCyberExposureVectors = useMemo<CyberExposureVector[]>(
    () => cyberExposure?.exposure?.exposure_vectors ?? [],
    [cyberExposure],
  );
  const cyberHasPenaltyData = useMemo(
    () =>
      cyberExposure?.has_coverage === true &&
      visibleCyberExposureVectors.length > 0,
    [cyberExposure?.has_coverage, visibleCyberExposureVectors.length],
  );
  const hideCybersecurityComplianceRisk = !cyberHasPenaltyData;

  // When cyber loading starts, show hint after 4s; when it stops, clear hint
  useEffect(() => {
    if (!cyberLoading) {
      setCyberLoadingHint(false);
      return;
    }
    const t = setTimeout(() => setCyberLoadingHint(true), 4000);
    return () => clearTimeout(t);
  }, [cyberLoading]);

  useEffect(() => {
    if (
      hideCybersecurityComplianceRisk &&
      expanded === "cybersecurityComplianceRisk"
    ) {
      setExpanded(false);
    }
  }, [expanded, hideCybersecurityComplianceRisk]);

  // Fetch cybersecurity exposure automatically so unsupported facility types never flash a tab.
  useEffect(() => {
    if (!cyberFacilityType || !cyberCountryCode) {
      setCyberExposure(null);
      setCyberError(null);
      setCyberLoading(false);
      return;
    }
    let cancelled = false;
    setCyberExposure(null);
    setCyberError(null);
    setCyberLoading(true);
    if (cyberLoadingTimeoutRef.current) {
      clearTimeout(cyberLoadingTimeoutRef.current);
      cyberLoadingTimeoutRef.current = null;
    }
    // Guard: if the request never settles, stop loading after 15s and show error
    cyberLoadingTimeoutRef.current = setTimeout(() => {
      if (!cancelled) {
        setCyberError(
          "Request timed out. Ensure REACT_APP_API_BASEURL points to your backend (e.g. http://localhost:8000) and CORS allows this origin.",
        );
        setCyberLoading(false);
      }
    }, 15000);
    facilityComplianceService
      .getCybersecurityPenaltyExposure({
        facility_type: cyberFacilityType,
        country_code: cyberCountryCode,
        vertical: "healthcare",
        as_of_date: cyberRefDate || undefined,
      })
      .then((data) => {
        if (!cancelled) {
          setCyberExposure(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCyberError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (cyberLoadingTimeoutRef.current) {
          clearTimeout(cyberLoadingTimeoutRef.current);
          cyberLoadingTimeoutRef.current = null;
        }
        if (!cancelled) {
          setCyberLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (cyberLoadingTimeoutRef.current) {
        clearTimeout(cyberLoadingTimeoutRef.current);
        cyberLoadingTimeoutRef.current = null;
      }
    };
  }, [
    cyberFacilityType,
    cyberCountryCode,
    cyberRefDate,
    cyberRefreshNonce,
  ]);

  // Content-only signature so profile saves update counts even when Penalty stays mounted off-tab.
  const selectedFacilityAccreditingSignature = useMemo(() => {
    const bodies = selectedFacility?.accrediting_body ?? [];
    return bodies
      .map((b) => {
        if (typeof b === "string") return b.trim();
        return String(b.slug ?? "").trim();
      })
      .filter(Boolean)
      .sort()
      .join("|");
  }, [selectedFacility?.accrediting_body]);

  // Accrediting-body slugs for the new Accreditations sub-tab.
  // When a DB-fresh fetch has completed, use those slugs so the tab reflects the
  // latest DB value even while the profile setup dialog is still open.
  // Fall back to selectedFacility.accrediting_body until the fetch completes.
  const accreditingBodySlugs = useMemo(() => {
    if (freshAccreditingBodySlugs !== null) return freshAccreditingBodySlugs;
    return (selectedFacility?.accrediting_body ?? [])
      .map((b) => {
        const raw =
          typeof b === "string" ? b : (b.slug ?? "");
        return normalizeAccreditingBodySlug(raw);
      })
      .filter((s): s is string => Boolean(s));
  }, [freshAccreditingBodySlugs, selectedFacility?.accrediting_body]);

  /**
   * Tab badge: total accreditation programs returned for the selected bodies — not the
   * count of bodies. Stays 0 (badge hidden) until `accreditationsData` loads so we never
   * show “1” when the catalog has zero programs for that body/facility type.
   */
  const accredProgramsTotalForBadge = useMemo(() => {
    if (accreditationsData === null) return null;
    let n = 0;
    for (const body of Object.values(accreditationsData)) {
      n += (body as BodyProgramData).programs?.length ?? 0;
    }
    return n;
  }, [accreditationsData]);

  const recurringCostsTabCount = useMemo(
    () =>
      penaltyData?.rollup?.recurringAnnualCosts?.filter((c) => c != null)
        .length ?? 0,
    [penaltyData?.rollup?.recurringAnnualCosts],
  );

  /** Hide Accreditations when there are no program rows; keep visible while loading or on error. */
  const showAccreditationsTab = useMemo(() => {
    if (accreditingBodySlugs.length === 0) return false;
    if (accreditationsError || accreditationsLoading) return true;
    if (accredProgramsTotalForBadge === null) return true;
    return accredProgramsTotalForBadge > 0;
  }, [
    accreditingBodySlugs.length,
    accreditationsError,
    accreditationsLoading,
    accredProgramsTotalForBadge,
  ]);

  useEffect(() => {
    if (penaltySubTab === "recurring" && recurringCostsTabCount === 0) {
      setPenaltySubTab("penalties");
    }
    if (penaltySubTab === "accreditations" && !showAccreditationsTab) {
      setPenaltySubTab("penalties");
    }
  }, [penaltySubTab, recurringCostsTabCount, showAccreditationsTab]);

  // Clear accreditations cache and fresh-body state when facility / jurisdiction changes
  useEffect(() => {
    setAccreditationsData(null);
    setAccreditationsError(null);
    setFreshAccreditingBodySlugs(null);
    accreditingBodyFetchFacilityRef.current = null;
  }, [cyberFacilityType, cyberCountryCode, selectedFacility?.facility_id]);

  const refetchAccreditingBodySlugsForFacility = useCallback(
    (facilityId: number) => {
      accreditingBodyFetchFacilityRef.current = facilityId;
      return getAccreditingBody(facilityId)
        .then((data) => {
          if (accreditingBodyFetchFacilityRef.current !== facilityId) return;
          const slugs = (data.accrediting_body ?? [])
            .map((b) => normalizeAccreditingBodySlug(b.slug ?? ""))
            .filter((s): s is string => Boolean(s));
          setFreshAccreditingBodySlugs(slugs);
        })
        .catch(() => {
          if (accreditingBodyFetchFacilityRef.current === facilityId) {
            setFreshAccreditingBodySlugs([]);
          }
        });
    },
    [],
  );

  // When accrediting bodies change on the selected facility (e.g. after Facility Profile save),
  // drop cached DB slugs and refetch. Penalty often remains mounted with display:none while the
  // user edits the profile, so the accordion "open" transition effect does not run again.
  useEffect(() => {
    if (!penaltyTabEverActive) return;
    const facilityId = selectedFacility?.facility_id;
    if (!facilityId) return;
    const key = `${facilityId}:${selectedFacilityAccreditingSignature}`;
    if (prevFacilityAccreditingKeyRef.current === key) return;
    const isInitialKey = prevFacilityAccreditingKeyRef.current === "";
    prevFacilityAccreditingKeyRef.current = key;
    if (isInitialKey) return;

    setAccreditationsData(null);
    setAccreditationsError(null);
    setFreshAccreditingBodySlugs(null);
    void refetchAccreditingBodySlugsForFacility(facilityId);
  }, [
    selectedFacilityAccreditingSignature,
    selectedFacility?.facility_id,
    refetchAccreditingBodySlugsForFacility,
    penaltyTabEverActive,
  ]);

  // Whenever the user opens "Potential Penalties & Compliance Risks", reload
  // accrediting bodies from the DB and drop cached program rows so the Accreditations
  // sub-tab cannot show stale data after a Facility Profile save.
  useEffect(() => {
    if (!penaltyTabEverActive) return;
    const isOpen = expanded === "penaltiesAndTotalExposure";
    const wasOpen = prevPenaltiesAccordionExpandedRef.current;
    prevPenaltiesAccordionExpandedRef.current = isOpen;
    if (!isOpen || wasOpen) return;

    setAccreditationsData(null);
    setAccreditationsError(null);
    setFreshAccreditingBodySlugs(null);

    const facilityId = selectedFacility?.facility_id;
    if (!facilityId) return;

    void refetchAccreditingBodySlugsForFacility(facilityId);
  }, [
    expanded,
    selectedFacility?.facility_id,
    refetchAccreditingBodySlugsForFacility,
    penaltyTabEverActive,
  ]);

  // Same refresh when switching onto the Accreditations sub-tab (accordion already open).
  useEffect(() => {
    if (!penaltyTabEverActive) return;
    const prev = prevPenaltySubTabRef.current;
    prevPenaltySubTabRef.current = penaltySubTab;
    if (expanded !== "penaltiesAndTotalExposure") return;
    if (penaltySubTab !== "accreditations") return;
    if (prev === "accreditations") return;

    setAccreditationsData(null);
    setAccreditationsError(null);
    setFreshAccreditingBodySlugs(null);

    const facilityId = selectedFacility?.facility_id;
    if (!facilityId) return;

    void refetchAccreditingBodySlugsForFacility(facilityId);
  }, [
    penaltySubTab,
    expanded,
    selectedFacility?.facility_id,
    refetchAccreditingBodySlugsForFacility,
    penaltyTabEverActive,
  ]);

  // Load accreditation program rows when the accordion is expanded (not only when the
  // Accreditations sub-tab is selected) so counts and content stay aligned after profile edits.
  useEffect(() => {
    if (expanded !== "penaltiesAndTotalExposure") return;
    if (
      accreditationsData !== null ||
      accreditationsLoading ||
      accreditingBodySlugs.length === 0
    ) {
      return;
    }
    let cancelled = false;
    setAccreditationsError(null);
    setAccreditationsLoading(true);
    facilityComplianceService
      .getMultiAccreditationPrograms({
        bodySlugs: accreditingBodySlugs,
        facilityType: cyberFacilityType,
        countryCode: cyberCountryCode,
      })
      .then((data) => {
        if (!cancelled) setAccreditationsData(data);
      })
      .catch((err) => {
        if (!cancelled)
          setAccreditationsError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setAccreditationsLoading(false);
      });
    return () => {
      cancelled = true;
      // Strict Mode (and dependency churn) can tear down this effect while the request
      // is still in flight. The finally() above skips clearing loading when cancelled,
      // which would leave accreditationsLoading stuck true and block the next effect run
      // (early return checks accreditationsLoading). Always reset on cleanup.
      setAccreditationsLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- accreditationsLoading omitted intentionally
  }, [
    expanded,
    accreditationsData,
    accreditingBodySlugs,
    cyberFacilityType,
    cyberCountryCode,
  ]);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const closeCitationPanel = () => {
    setSelectedCitation(null);
  };

  const openSomSectionDialog = (tag: string) => {
    setSomSectionDialogTag(tag);
    setSomSectionDialogOpen(true);
  };

  const closeSomSectionDialog = () => {
    setSomSectionDialogOpen(false);
    setSomSectionDialogTag(null);
  };

  // Compute projected min/max for a cyber exposure vector (uses applied values after Calculate)
  const computeCyberProjected = useCallback(
    (vec: CyberExposureVector): { min: number; max: number } => {
      const ps = vec.penalty_structure ?? {};
      let minVal = 0;
      let maxVal = 0;
      const hasViol =
        ps.per_violation_min != null || ps.per_violation_max != null;
      const hasDay = ps.per_day_min != null || ps.per_day_max != null;
      if (hasViol && cyberAppliedViolations > 0) {
        minVal += (ps.per_violation_min ?? 0) * cyberAppliedViolations;
        maxVal += (ps.per_violation_max ?? 0) * cyberAppliedViolations;
      }
      if (hasDay && cyberAppliedDays > 0) {
        minVal += (ps.per_day_min ?? 0) * cyberAppliedDays;
        maxVal += (ps.per_day_max ?? 0) * cyberAppliedDays;
      }
      const cap = ps.annual_cap;
      if (cap != null && maxVal > cap) maxVal = cap;
      if (cap != null && minVal > cap) minVal = cap;
      return { min: minVal, max: maxVal };
    },
    [cyberAppliedViolations, cyberAppliedDays],
  );

  /** Compute projected with given violations/days (for change detection on Calculate). */
  const computeCyberProjectedWithParams = useCallback(
    (vec: CyberExposureVector, violations: number, days: number): { min: number; max: number } => {
      const ps = vec.penalty_structure ?? {};
      let minVal = 0;
      let maxVal = 0;
      const hasViol =
        ps.per_violation_min != null || ps.per_violation_max != null;
      const hasDay = ps.per_day_min != null || ps.per_day_max != null;
      if (hasViol && violations > 0) {
        minVal += (ps.per_violation_min ?? 0) * violations;
        maxVal += (ps.per_violation_max ?? 0) * violations;
      }
      if (hasDay && days > 0) {
        minVal += (ps.per_day_min ?? 0) * days;
        maxVal += (ps.per_day_max ?? 0) * days;
      }
      const cap = ps.annual_cap;
      if (cap != null && maxVal > cap) maxVal = cap;
      if (cap != null && minVal > cap) minVal = cap;
      return { min: minVal, max: maxVal };
    },
    [],
  );

  const handleCyberCalculate = useCallback(() => {
    const vectors = cyberExposure?.exposure?.exposure_vectors ?? [];
    const changed = new Set<number>();
    for (let i = 0; i < vectors.length; i++) {
      const oldP = computeCyberProjectedWithParams(
        vectors[i],
        cyberAppliedViolations,
        cyberAppliedDays,
      );
      const newP = computeCyberProjectedWithParams(
        vectors[i],
        cyberViolations,
        cyberDays,
      );
      if (oldP.min !== newP.min || oldP.max !== newP.max) changed.add(i);
    }
    setCyberHighlightedRows(changed);
    if (cyberHighlightTimeoutRef.current) {
      clearTimeout(cyberHighlightTimeoutRef.current);
    }
    cyberHighlightTimeoutRef.current = setTimeout(() => {
      setCyberHighlightedRows(new Set());
      cyberHighlightTimeoutRef.current = null;
    }, 5000);
    setCyberAppliedViolations(cyberViolations);
    setCyberAppliedDays(cyberDays);
  }, [
    cyberViolations,
    cyberDays,
    cyberAppliedViolations,
    cyberAppliedDays,
    cyberExposure?.exposure?.exposure_vectors,
    computeCyberProjectedWithParams,
  ]);

  const handleCyberRefresh = useCallback(() => {
    setCyberRefreshNonce((prev) => prev + 1);
  }, []);

  const penaltyPageAuditLoggedRef = React.useRef<string | null>(null);

  // Log page access when the Penalty tab is visible
  useEffect(() => {
    if (!penaltyTabActive) {
      penaltyPageAuditLoggedRef.current = null;
      return;
    }
    const facility = selectedFacility || getSelectedFacilityFromSession();
    const facilityId = facility?.facility_id;
    if (!facilityId) return;
    const key = String(facilityId);
    if (penaltyPageAuditLoggedRef.current === key) return;
    penaltyPageAuditLoggedRef.current = key;

    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);

      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: "/api/v1/penalty/",
        method: "GET",
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "Potential Penalties",
          action: "VIEW",
        },
        action_type: "VIEW",
        action_status: "SUCCESS",
      });
    };

    void logPageAccess();
  }, [penaltyTabActive, selectedFacility]);

  const getFacilityData = useCallback(
    () => selectedFacility || getSelectedFacilityFromSession<Facility>(),
    [selectedFacility],
  );

  const { connectToAffableBPM: handleConnectAffableBPM, isConnectingAffable } =
    useAffableConnect({ getFacilityData, workflowType: "Penalty" });

  // Resolve facility once for render-time checks (e.g., Affable connection status)
  const resolvedFacility = useMemo(() => getFacilityData(), [getFacilityData]);
  const isAffableConnected = Boolean(
    (resolvedFacility as any)?.is_affable_connected,
  );

  const handleDownloadXLS = async (exportCalendar?: any[]) => {
    const sourceCalendar =
      exportCalendar && exportCalendar.length > 0
        ? exportCalendar
        : penaltyData?.complianceCalendar ?? [];
    if (sourceCalendar.length === 0) {
      return;
    }

    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Compliance Calendar");

    const includeSomTagsColumn = sourceCalendar.some(
      complianceCalendarItemHasSomTagsData,
    );

    // Define columns (omit Survey Tags when every row is blank — matches on-screen table)
    worksheet.columns = [
      { header: "Deadline", key: "deadline", width: 15 },
      { header: "Status", key: "status", width: 15 },
      { header: "Items", key: "items", width: 35 },
      { header: "Requirement", key: "requirement", width: 35 },
      { header: "Source", key: "source", width: 25 },
      { header: "Penalty if Missed", key: "penalty", width: 20 },
      { header: "Est. Cost Exposure", key: "exposure", width: 20 },
      ...(includeSomTagsColumn
        ? [{ header: "Survey Tags (SOM)", key: "somTags", width: 22 }]
        : []),
      { header: "Historic Penalties", key: "historic", width: 28 },
    ];

    // Export the same quarter window shown in the compliance calendar tabs, plus
    // ongoing/standing-duty items (which live in the "Upcoming" tab, not in any quarter).
    const quarterItems = sourceCalendar.filter(
      (item: any) =>
        isOngoingCalendarItem(item) ||
        quarterConfig.some((q) => isDateInQuarter(item.deadline, q, item)),
    );

    // Add data rows (days computed at download time)
    quarterItems.forEach((calendarItem: any) => {
      const isOngoing =
        calendarItem.cadence === "ongoing" ||
        calendarItem.deadlineType === "none";
      const deadline = isOngoing
        ? null
        : complianceDeadlineToDate(calendarItem.deadline);
      const today = new Date();
      const daysUntilDeadline =
        deadline != null
          ? differenceInDays(startOfDay(deadline), startOfDay(today))
          : 0;

      const isRequired = calendarItem.required !== false;
      let statusLabel: string;
      if (isOngoing) {
        statusLabel = "Ongoing obligation";
      } else if (isRequired && daysUntilDeadline < 0) {
        statusLabel = "High Risk";
      } else if (isRequired) {
        statusLabel = "Required";
      } else {
        statusLabel = "Recommended";
      }

      const evs = (calendarItem as any).historicPenaltyEvents;
      const hist = (calendarItem as any).historicPenaltiesByYear;
      const historicStr =
        Array.isArray(evs) && evs.length > 0
          ? evs
              .map((ev: HistoricPenaltyEventDetail) => {
                const amt = formatCurrencyAmountWhole(ev.amount);
                return `${ev.actionDate ?? "—"}: ${amt}`;
              })
              .join("; ")
          : Array.isArray(hist) && hist.length > 0
            ? hist
                .map(
                  (h: {
                    year?: number;
                    amount?: number | string | null;
                    amountText?: string | null;
                  }) => {
                    const hasAmt =
                      h.amount != null &&
                      !(typeof h.amount === "string" && h.amount.trim() === "");
                    const amt = hasAmt
                      ? formatCurrencyAmountWhole(h.amount)
                      : h.amountText || "";
                    return `${h.year}: ${amt}`;
                  },
                )
                .join("; ")
            : "";

      const somTagsCell =
        Array.isArray((calendarItem as any).somTags) &&
        (calendarItem as any).somTags.length > 0
          ? (calendarItem as any).somTags.join(", ")
          : "";
      const row = worksheet.addRow({
        deadline: isOngoing
          ? "Ongoing (no fixed due date)"
          : `${formatDate(calendarItem.deadline)}${daysUntilDeadline < 0 ? " (OVERDUE)" : ` (${daysUntilDeadline} Days)`}`,
        status: statusLabel,
        items: calendarItem.item,
        requirement: calendarItem.requirement,
        source: calendarItem.citation?.publisher || "Government Authority",
        penalty: formatCurrency(calendarItem.penaltyIfMissed),
        exposure: formatCurrency(calendarItem.estimatedCostExposure),
        ...(includeSomTagsColumn ? { somTags: somTagsCell } : {}),
        historic: historicStr,
      });

      // Style the Requirement cell as a hyperlink with italic and underline
      const requirementCell = row.getCell("requirement");
      if (calendarItem.citation?.url) {
        requirementCell.value = {
          text: calendarItem.requirement,
          hyperlink: calendarItem.citation.url,
          tooltip: "Click to view requirement details",
        };
        requirementCell.font = {
          underline: true,
          color: { argb: "FF0563C1" }, // Blue color
        };
      }
    });

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "left" };

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const labels =
      quarterConfig.length > 0
        ? quarterConfig.map((q) => q.label.replace(/\s/g, "_")).join("_")
        : "Compliance_Calendar";
    link.download = `${labels}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          minHeight: "40vh",
        }}
      >
        <CircularProgress size={48} sx={{ color: "#0a829d" }} />
        <Typography sx={{ color: "#737373", fontSize: 18, fontWeight: 500 }}>
          Loading penalty data...
        </Typography>
        <Typography
          sx={{
            color: "#737373",
            fontSize: 16,
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          This AI-assisted analysis may take a few minutes.
        </Typography>
      </Box>
    );
  }

  if (penaltiesLoading && (!penaltyData || !(penaltyData.complianceCalendar?.length))) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          minHeight: "200px",
        }}
      >
        <CircularProgress size={36} sx={{ color: "#0a829d" }} />
        <Typography sx={{ color: "#737373", fontSize: 16 }}>
          Loading Potential Penalties...
        </Typography>
      </Box>
    );
  }

  if (!penaltyData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <Typography>No penalty data available</Typography>
      </Box>
    );
  }

  const visibleTabs = [...quarterConfig.map((q) => q.label), "Upcoming"];

  const hasActiveCitation = selectedCitation !== null;

  const isGlobalAdmin = isGlobalAdministrator();
  // Align with dashboard: CMS NH PDC always, or any facility type with historic year data (#10126).
  const showHistoricPenaltiesForFacility =
    isGlobalAdmin ||
    shouldShowFacilityHistoricPenaltiesSection(
      selectedFacility,
      cyberFacilityType,
      historicPenaltiesFacility ?? undefined,
    );
  const showHistoricPenaltiesSection =
    isHealthcareVertical &&
    isUsaForHistoricPenalties &&
    historicPenaltiesAccordionControlsEnabled &&
    showHistoricPenaltiesForFacility &&
    (isGlobalAdmin ||
      userCanAccessHistoricPenalties() ||
      Boolean((penaltiesLlmPrompt ?? "").trim()));
  const HistoricPenaltiesPanel = HistoricPenaltiesSection;

  return (
    <PenaltyContainer>
      <ComplianceHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M8.5 6.5V15.5H11.5V6.5C11.5 5.94772 11.0523 5.5 10.5 5.5H9.5C8.94772 5.5 8.5 5.94772 8.5 6.5ZM7.5 9.5V6.5C7.5 5.39543 8.39543 4.5 9.5 4.5H10.5C11.6046 4.5 12.5 5.39543 12.5 6.5V7.5H14C15.1046 7.5 16 8.39543 16 9.5V16C16 16.2761 15.7761 16.5 15.5 16.5H4.5C4.22386 16.5 4 16.2761 4 16V11.5C4 10.3954 4.89543 9.5 6 9.5H7.5ZM7.5 15.5V10.5H6C5.44772 10.5 5 10.9477 5 11.5V15.5H7.5ZM12.5 15.5H15V9.5C15 8.94772 14.5523 8.5 14 8.5H12.5V15.5Z"
              fill="#065B70"
            />
          </svg>
          <Typography
            sx={{
              color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
            }}
          >
            Potential Penalties
          </Typography>
        </Box>
        {showWatchGuide && (
          <Button
          variant="outlined"
          disabled={!onWatchGuideClick || watchGuideLoading}
          onClick={onWatchGuideClick}
          sx={{
            display: "flex",
            flexShrink: 0,
            minHeight: "40px",
            padding: "8px 16px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "8px",
            border: "1px solid #5BBFD6",
            background: "#FFFFFF",
            boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F8FDFF",
              borderColor: "#3CA9C2",
            },
          }}
        >
          {watchGuideIconSrc ? (
            <Box
              component="img"
              src={watchGuideIconSrc}
              alt="Watch guide"
              sx={{ width: 16, height: 16, display: "block" }}
            />
          ) : null}
          <Typography
            sx={{
              color: "#086E86",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: "normal",
            }}
          >
            {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
          </Typography>
          </Button>
        )}
      </ComplianceHeader>
      {/* <Typography
        sx={{
          color: "#525252",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          ml: "28px",
        }}
      >
        AI-assisted analysis. Human review and verification required.
      </Typography> */}
      <MainContent sx={{ mt: "12px" }}>
        <ContentContainer $hasActiveReport={hasActiveCitation}>
          {/* Compliance Calendar with Penalty Exposure - closed by default */}
          {complianceCalendarAccordions.map((cfg) => (
              <StyledAccordion
                key={cfg.accordionKey}
                expanded={expanded === cfg.accordionKey}
                onChange={handleAccordionChange(cfg.accordionKey)}
              >
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    alignItems: "center",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      gap: 1,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ width: "20px", height: "20px", flexShrink: 0 }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5.5 17C4.11929 17 3 15.8807 3 14.5V10.5C3 10.2239 3.22386 10 3.5 10C3.77614 10 4 10.2239 4 10.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V6.99997L15.9941 7L6.4913 7C6.4913 6.64209 6.364 6.28417 6.10939 6H15.9941L16 6.00003V5.5C16 4.67157 15.3284 4 14.5 4H5.5C4.67157 4 4 4.67157 4 5.5V7.29289L4.64645 6.64645C4.84171 6.45118 5.15829 6.45118 5.35355 6.64645C5.54882 6.84171 5.54882 7.15829 5.35355 7.35355L3.85355 8.85355C3.65829 9.04882 3.34171 9.04882 3.14645 8.85355L1.64645 7.35355C1.45118 7.15829 1.45118 6.84171 1.64645 6.64645C1.84171 6.45118 2.15829 6.45118 2.35355 6.64645L3 7.29289V5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5ZM8 10C8 10.5523 7.55228 11 7 11C6.44772 11 6 10.5523 6 10C6 9.44771 6.44772 9 7 9C7.55228 9 8 9.44771 8 10ZM7 14C7.55228 14 8 13.5523 8 13C8 12.4477 7.55228 12 7 12C6.44772 12 6 12.4477 6 13C6 13.5523 6.44772 14 7 14ZM11 10C11 10.5523 10.5523 11 10 11C9.44771 11 9 10.5523 9 10C9 9.44771 9.44771 9 10 9C10.5523 9 11 9.44771 11 10ZM10 14C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12C9.44771 12 9 12.4477 9 13C9 13.5523 9.44771 14 10 14ZM14 10C14 10.5523 13.5523 11 13 11C12.4477 11 12 10.5523 12 10C12 9.44771 12.4477 9 13 9C13.5523 9 14 9.44771 14 10Z"
                          fill="#065B70"
                        />
                      </svg>
                    </Box>
                    <AccordionTitle
                      className="accordion-title"
                      sx={{ lineHeight: 1.25, flex: 1, minWidth: 0 }}
                    >
                      {cfg.title}
                    </AccordionTitle>
                    {cfg.accordionKey === "complianceCalendar" && isCmsNursingHomePdc ? (
                      <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                        <CmsNursingHomePdcInfoTooltip />
                      </Box>
                    ) : null}
                    {cfg.showLlmButton && showPenaltiesLlmOnCalendarAccordion && (
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{ flexShrink: 0 }}
                      >
                        <GlobalAdminLlmPromptButton
                          dialogTitle="Penalties & compliance calendar — LLM prompt"
                          promptText={penaltiesLlmPrompt}
                          wallTimeSec={penaltiesLlmWallSec ?? undefined}
                        />
                      </Box>
                    )}
                  </Box>
                  {isAffableConnected && (
                    <Button
                      variant="outlined"
                      disabled={isConnectingAffable}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConnectAffableBPM();
                      }}
                      sx={{
                        display: "flex",
                        padding: "4px 12px",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "6px",
                        borderRadius: "8px",
                        minHeight: "32px",
                        height: "32px",
                        marginRight: "8px",
                        border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                        background:
                          "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        "&:hover": {
                          background: "#f5fdff",
                          borderColor: "#065b70",
                          boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                        },
                        "&:active": {
                          background: "#e5f9fd",
                        },
                      }}
                    >
                      <AffableConnectedIcon />
                      <Typography
                        sx={{
                          color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "13px",
                          fontStyle: "normal",
                          fontWeight: 600,
                          lineHeight: "normal",
                          textTransform: "none",
                        }}
                      >
                        {isConnectingAffable
                          ? "Connecting..."
                          : "Connect with AffableFLOW"}
                      </Typography>
                    </Button>
                  )}
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: "#ffffff",
                    padding: "12px",
                    position: "relative",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#f8fdfe",
                      padding: "4px 8px 8px 8px",
                      borderRadius: "8px",
                      border: "1px solid #cff7ff",
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                    }}
                  >
                    {/* Quarter tabs + stats badges + Download XLS (single row) */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        flexWrap: "wrap",
                        borderBottom: "2px solid #e5e7eb",
                        marginBottom: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flex: "1 1 auto",
                          minWidth: 0,
                        }}
                      >
                        {visibleTabs.map((tabLabel) => {
                          const isSelected = selectedQuarter === tabLabel;
                          return (
                            <Box
                              key={tabLabel}
                              onClick={() => setSelectedQuarter(tabLabel)}
                              sx={{
                                padding: "10px 16px",
                                cursor: "pointer",
                                borderBottom: "2px solid",
                                borderColor: isSelected
                                  ? "#0a829d"
                                  : "transparent",
                                marginBottom: "-2px",
                                transition: "border-color 0.2s ease",
                                "&:hover": {
                                  borderColor: isSelected
                                    ? "#0a829d"
                                    : "#b2dfe8",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected ? "#0a829d" : "#737373",
                                  fontFamily: "Rethink Sans, sans-serif",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tabLabel}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                        }}
                      >
                        {!isUpcomingSelected && (
                          <>
                            {/* Active Items */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 14px",
                                borderRadius: "6px",
                                border: "1px solid #b2dfe8",
                                backgroundColor: "#eaf6f9",
                                cursor: "default",
                              }}
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                              >
                                <rect
                                  width="15"
                                  height="15"
                                  rx="3"
                                  fill="#0a829d"
                                />
                                <path
                                  d="M3.5 7.5L6 10L11 5"
                                  stroke="white"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#374151",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Active Items
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#0a829d",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                {cfg.filtered.length}
                              </Typography>
                            </Box>

                            {/* Due in 30 days */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 14px",
                                borderRadius: "6px",
                                border: "1px solid #b2dfe8",
                                backgroundColor: "#eaf6f9",
                                cursor: "default",
                              }}
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                              >
                                <rect
                                  x="1"
                                  y="2.5"
                                  width="13"
                                  height="11.5"
                                  rx="1.5"
                                  stroke="#0a829d"
                                  strokeWidth="1.3"
                                />
                                <path
                                  d="M4.5 1V4M10.5 1V4"
                                  stroke="#0a829d"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M1 6H14"
                                  stroke="#0a829d"
                                  strokeWidth="1.3"
                                />
                              </svg>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#374151",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Due in 30 days
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#0a829d",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                {
                                  cfg.filtered.filter(
                                    (item: any) => {
                                      const d = differenceInDays(
                                        startOfDay(complianceDeadlineToDate(item.deadline)),
                                        startOfDay(new Date()),
                                      );
                                      return d >= 0 && d <= 30;
                                    },
                                  ).length
                                }
                              </Typography>
                            </Box>

                            {/* High Risk */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 14px",
                                borderRadius: "6px",
                                border: "1px solid #b2dfe8",
                                backgroundColor: "#eaf6f9",
                                cursor: "default",
                              }}
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                              >
                                <path
                                  d="M7.5 2L13.5 12.5H1.5L7.5 2Z"
                                  fill="#0a829d"
                                  stroke="#0a829d"
                                  strokeWidth="0.5"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7.5 6V9"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <circle
                                  cx="7.5"
                                  cy="10.5"
                                  r="0.75"
                                  fill="white"
                                />
                              </svg>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#374151",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                High Risk
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#0a829d",

                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                {
                                  cfg.filtered.filter(
                                    (item: any) => {
                                      const d = differenceInDays(
                                        startOfDay(complianceDeadlineToDate(item.deadline)),
                                        startOfDay(new Date()),
                                      );
                                      return d < 0;
                                    },
                                  ).length
                                }
                              </Typography>
                            </Box>
                          </>
                        )}
                        <Button
                          variant="contained"
                          onClick={() => handleDownloadXLS(cfg.items)}
                          sx={{
                            display: "flex",
                            padding: "12px 24px",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px",
                            borderRadius: "8px",
                            textTransform: "none",
                            minHeight: "44px",
                            height: "44px",
                            marginBottom: "4px",
                            background:
                              "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
                            boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
                            "&:hover": {
                              backgroundColor: "#065b70",
                              boxShadow: "0px 4px 8px rgba(6, 91, 112, 0.3)",
                              border: "1px solid #065b70",
                            },
                            "&:active": {
                              backgroundColor: "#065b70",
                              boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.2)",
                            },
                          }}
                        >
                          Download XLS
                        </Button>
                      </Box>
                    </Box>

                    {/* Calendar Table */}
                        {/* Calendar Table or Upcoming message */}
                        <>
                      {isUpcomingSelected ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "200px",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              gap: "12px",
                              padding: "12px 16px",
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#f9fafb",
                              height: "50px",
                              maxWidth: "560px",
                            }}
                          >
                            <Box
                              sx={{
                                width: "20px",
                                height: "20px",
                                flexShrink: 0,
                                color: "#0a829d",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginTop: "2px",
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="9"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  fill="none"
                                />
                                <path
                                  d="M10 6v1M10 10v4"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#374151",
                                fontFamily: "Rethink Sans, sans-serif",
                                lineHeight: 1.5,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: "#0a829d",
                                  fontWeight: 600,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Future quarter data
                              </Typography>{" "}
                              will become available as it is released, provided
                              your account remains active.
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                    <>
                      <Box
                        sx={{
                          border: "1px solid #0a829d",
                          borderRadius: "4px",
                          /** Do not use overflow:hidden here — it can clip the inner scrollbars and affect sticky layout. */
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                        }}
                      >
                        {cfg.filtered.length === 0 ? (
                          <Box
                            sx={{
                              padding: "32px 16px",
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#737373",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {`No compliance items due in ${selectedQuarter}`}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            {/** Horizontal scroll wraps header + body so wide tables stay aligned; vertical scroll is body-only so the scrollbar does not cross the teal header. */}
                            <Box sx={{ overflowX: "auto", width: "100%" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  minWidth: 0,
                                  width: "100%",
                                }}
                              >
                            {/** Teal header sits outside the vertical scroll region. Extra right padding lines up column edges with the row grid beside the scrollbar (~10px track). */}
                            <Box
                              sx={{
                                backgroundColor: "#0a829d",
                                width: "100%",
                                pl: "16px",
                                pr: "26px",
                                boxSizing: "border-box",
                                borderTopLeftRadius: "4px",
                                borderTopRightRadius: "4px",
                                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    cfg.complianceCalendarGridTemplateColumns,
                                  columnGap: "12px",
                                  width: "100%",
                                  maxWidth: "100%",
                                  minWidth: 0,
                                  boxSizing: "border-box",
                                  py: "12px",
                                  alignItems: "center",
                                  "& > *": { minWidth: 0 },
                                }}
                              >
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Deadline
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px", textAlign: "left" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Status
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Items
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Requirement
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Source
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Penalty if Missed
                                  </Typography>
                                </Box>
                                <Box sx={{ px: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      color: "white",
                                    }}
                                  >
                                    Est. Cost Exposure
                                  </Typography>
                                </Box>
                                {cfg.showSomTagsColumn && (
                                  <Box sx={{ px: "4px" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        fontWeight: 400,
                                        color: "white",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      Survey Tags (SOM)
                                    </Typography>
                                  </Box>
                                )}
                                {cfg.showHistoricPenaltiesColumn && (
                                  <Box sx={{ px: "4px" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        fontWeight: 400,
                                        color: "white",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      Historic Penalties
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                maxHeight: "min(65vh, 640px)",
                                minHeight: 0,
                                flex: "1 1 auto",
                                overflowY: "auto",
                                overflowX: "visible",
                                borderBottomLeftRadius: "4px",
                                borderBottomRightRadius: "4px",
                                scrollbarGutter: "stable",
                                "&::-webkit-scrollbar": {
                                  width: "10px",
                                  height: "10px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                  backgroundColor: "rgba(10, 130, 157, 0.4)",
                                  borderRadius: "5px",
                                },
                                "&::-webkit-scrollbar-track": {
                                  backgroundColor: "rgba(0, 0, 0, 0.06)",
                                },
                              }}
                            >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  cfg.complianceCalendarGridTemplateColumns,
                                columnGap: "12px",
                                width: "100%",
                                maxWidth: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                                px: "16px",
                                alignItems: "start",
                                backgroundColor: "#ffffff",
                                /** Without this, grid items use min-width: auto and long text overlaps the next column. */
                                "& > *": { minWidth: 0 },
                              }}
                            >
                          {cfg.filtered.map(
                            (calendarItem: any, index: number) => {
                              const isOngoing =
                                calendarItem.cadence === "ongoing" ||
                                calendarItem.deadlineType === "none";
                              const deadline = isOngoing
                                ? null
                                : complianceDeadlineToDate(
                                    calendarItem.deadline,
                                  );
                              const today = new Date();
                              const daysUntilDeadline =
                                deadline != null
                                  ? differenceInDays(
                                      startOfDay(deadline),
                                      startOfDay(today),
                                    )
                                  : 0;
                              const isRequired = calendarItem.required !== false;
                              const isOverdue =
                                !isOngoing && deadline != null && daysUntilDeadline < 0;
                              const isHighRisk = isRequired && isOverdue;
                              const penaltyFormatted = formatCurrency(
                                calendarItem.penaltyIfMissed,
                              );
                              const penaltyTipEnabled = Boolean(
                                penaltyFormatted?.trim(),
                              );

                              const rowDividerSx =
                                index > 0
                                  ? { borderTop: "1px solid #e5f5f8" }
                                  : {};

                              return (
                                <Box
                                  key={index}
                                  data-testid="compliance-calendar-row"
                                  sx={{ display: "contents" }}
                                >
                                  {/* Deadline column: date + days/overdue badge */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      gap: "4px",
                                      paddingX: "1px",
                                      paddingTop: "2px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "1px",
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        style={{ flexShrink: 0 }}
                                      >
                                        <path
                                          d="M5.5 17C4.11929 17 3 15.8807 3 14.5V10.5C3 10.2239 3.22386 10 3.5 10C3.77614 10 4 10.2239 4 10.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V6.99997L15.9941 7L6.4913 7C6.4913 6.64209 6.364 6.28417 6.10939 6H15.9941L16 6.00003V5.5C16 4.67157 15.3284 4 14.5 4H5.5C4.67157 4 4 4.67157 4 5.5V7.29289L4.64645 6.64645C4.84171 6.45118 5.15829 6.45118 5.35355 6.64645C5.54882 6.84171 5.54882 7.15829 5.35355 7.35355L3.85355 8.85355C3.65829 9.04882 3.34171 9.04882 3.14645 8.85355L1.64645 7.35355C1.45118 7.15829 1.45118 6.84171 1.64645 6.64645C1.84171 6.45118 2.15829 6.45118 2.35355 6.64645L3 7.29289V5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5ZM8 10C8 10.5523 7.55228 11 7 11C6.44772 11 6 10.5523 6 10C6 9.44771 6.44772 9 7 9C7.55228 9 8 9.44771 8 10ZM7 14C7.55228 14 8 13.5523 8 13C8 12.4477 7.55228 12 7 12C6.44772 12 6 12.4477 6 13C6 13.5523 6.44772 14 7 14ZM11 10C11 10.5523 10.5523 11 10 11C9.44771 11 9 10.5523 9 10C9 9.44771 9.44771 9 10 9C10.5523 9 11 9.44771 11 10ZM10 14C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12C9.44771 12 9 12.4477 9 13C9 13.5523 9.44771 14 10 14ZM14 10C14 10.5523 13.5523 11 13 11C12.4477 11 12 10.5523 12 10C12 9.44771 12.4477 9 13 9C13.5523 9 14 9.44771 14 10Z"
                                          fill="#065B70"
                                        />
                                      </svg>
                                      <Typography
                                        data-testid="compliance-calendar-deadline"
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: "#262626",
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        {isOngoing
                                          ? "Ongoing"
                                          : formatDate(calendarItem.deadline)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        backgroundColor: isOngoing
                                          ? "#f0f4c3"
                                          : isOverdue
                                          ? "#fce4ec"
                                          : "#99d4e1",
                                        borderRadius: "2px",
                                        padding: "1px 6px",
                                        textAlign: "center",
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "11px",
                                          fontWeight: 500,
                                          color: isOngoing
                                            ? "#33691e"
                                            : isOverdue
                                            ? "#c62828"
                                            : "#065b70",
                                          fontFamily: "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        {isOngoing
                                          ? "Standing duty"
                                          : isOverdue
                                          ? "OVERDUE"
                                          : `${daysUntilDeadline} Days`}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Status column */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-start",
                                      alignItems: "flex-start",
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      width: "100%",
                                      maxWidth: "100%",
                                      minWidth: 0,
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        backgroundColor: isHighRisk
                                          ? "#fce4ec"
                                          : isRequired
                                          ? "#e0f4f8"
                                          : "#fff8e1",
                                        borderRadius: "2px",
                                        padding: "2px 8px",
                                        textAlign: "left",
                                        boxSizing: "border-box",
                                        width: "max-content",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: isHighRisk
                                            ? "#c62828"
                                            : isRequired
                                            ? "#065b70"
                                            : "#7a5800",
                                          fontFamily: "Rethink Sans, sans-serif",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "3px",
                                          justifyContent: "flex-start",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {isHighRisk ? (
                                          <>
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 15 15"
                                              fill="none"
                                            >
                                              <path
                                                d="M7.5 2L13.5 12.5H1.5L7.5 2Z"
                                                fill="#c62828"
                                                stroke="#c62828"
                                                strokeWidth="0.5"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M7.5 6V9"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                              />
                                              <circle
                                                cx="7.5"
                                                cy="10.5"
                                                r="0.75"
                                                fill="white"
                                              />
                                            </svg>
                                            High Risk
                                          </>
                                        ) : isRequired ? (
                                          "Required"
                                        ) : (
                                          "Recommended"
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box
                                    sx={{
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#262626",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {calendarItem.item}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Box
                                      component="a"
                                      href={calendarItem.citation.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        display: "block",
                                        fontSize: "12px",
                                        color: "#065B70",
                                        textDecoration: "underline",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        cursor: "pointer",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {calendarItem.requirement}
                                    </Box>
                                  </Box>

                                  <Box
                                    sx={{
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#065b70",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {calendarItem.citation?.publisher ||
                                        "Government Authority"}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      minWidth: 0,
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Tooltip
                                      placement="top-start"
                                      enterDelay={350}
                                      disableHoverListener={!penaltyTipEnabled}
                                      title={
                                        penaltyTipEnabled ? (
                                          <CompliancePenaltyTooltipTitle
                                            text={penaltyFormatted}
                                          />
                                        ) : (
                                          ""
                                        )
                                      }
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "#fff5f3",
                                            color: "#7a1f24",
                                            maxWidth:
                                              "min(580px, calc(100vw - 32px))",
                                            p: 1.75,
                                            border: "1px solid #e8c4be",
                                            boxShadow:
                                              "0 8px 24px rgba(10, 40, 50, 0.12)",
                                          },
                                        },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: "#a4262c",
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          overflow: "hidden",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: "vertical",
                                          wordBreak: "break-word",
                                          cursor: penaltyTipEnabled
                                            ? "help"
                                            : "default",
                                        }}
                                      >
                                        ⚠️ {penaltyFormatted}
                                      </Typography>
                                    </Tooltip>
                                  </Box>

                                  <Box
                                    sx={{
                                      paddingX: "8px",
                                      py: "8px",
                                      minHeight: "64px",
                                      boxSizing: "border-box",
                                      ...rowDividerSx,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#262626",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      💰{" "}
                                      {formatCurrency(
                                        calendarItem.estimatedCostExposure,
                                      )}
                                    </Typography>
                                  </Box>

                                  {cfg.showSomTagsColumn && (
                                    <Box
                                      sx={{
                                        paddingX: "4px",
                                        py: "8px",
                                        minHeight: "64px",
                                        boxSizing: "border-box",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "4px",
                                        alignItems: "flex-start",
                                        justifyContent: "flex-start",
                                        ...rowDividerSx,
                                      }}
                                    >
                                      {(calendarItem as any).somTags?.length ? (
                                        (calendarItem as any).somTags.map(
                                          (tag: string, ti: number) => (
                                            <Tooltip
                                              key={ti}
                                              title={
                                                (calendarItem as any)
                                                  .surveyRiskSummary
                                                  ? `${(calendarItem as any).surveyRiskSummary} — Click for SOM details`
                                                  : `${tag} — Click for SOM details`
                                              }
                                            >
                                              <Chip
                                                label={tag}
                                                size="small"
                                                onClick={() =>
                                                  openSomSectionDialog(tag)
                                                }
                                                sx={{
                                                  fontSize: "10px",
                                                  height: "22px",
                                                  backgroundColor: "#e0f4f8",
                                                  color: "#065b70",
                                                  cursor: "pointer",
                                                  "&:hover": {
                                                    backgroundColor: "#c5e8f0",
                                                  },
                                                }}
                                              />
                                            </Tooltip>
                                          ),
                                        )
                                      ) : (
                                        <Typography
                                          sx={{
                                            fontSize: "11px",
                                            color: "#9e9e9e",
                                            fontFamily: "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          —
                                        </Typography>
                                      )}
                                    </Box>
                                  )}

                                  {cfg.showHistoricPenaltiesColumn && (
                                    <Box
                                      sx={{
                                        paddingX: "8px",
                                        py: "8px",
                                        minHeight: "64px",
                                        boxSizing: "border-box",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "4px",
                                        ...rowDividerSx,
                                      }}
                                    >
                                      {Array.isArray(
                                        (calendarItem as any).historicPenaltyEvents,
                                      ) &&
                                      (calendarItem as any).historicPenaltyEvents
                                        .length > 0 ? (
                                        (
                                          calendarItem as any
                                        ).historicPenaltyEvents.map(
                                          (
                                            ev: HistoricPenaltyEventDetail,
                                            hi: number,
                                          ) => {
                                            const label = `${ev.actionDate ?? "—"}: ${formatCurrencyAmountWhole(ev.amount)}`;
                                            return (
                                              <Link
                                                key={`${ev.actionId ?? hi}-${ev.actionDate ?? ""}-${ev.reference ?? ""}`}
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                  setHistoricPenaltyDialogEvent(ev)
                                                }
                                                sx={{
                                                  display: "block",
                                                  fontSize: "12px",
                                                  fontWeight: 400,
                                                  color: "#065B70",
                                                  textDecoration: "underline",
                                                  fontFamily:
                                                    "Rethink Sans, sans-serif",
                                                  textAlign: "left",
                                                  cursor: "pointer",
                                                  overflowWrap: "anywhere",
                                                  wordBreak: "break-word",
                                                  border: "none",
                                                  background: "none",
                                                  padding: 0,
                                                  marginBottom: "4px",
                                                  "&:last-of-type": {
                                                    marginBottom: 0,
                                                  },
                                                }}
                                              >
                                                {label}
                                              </Link>
                                            );
                                          },
                                        )
                                      ) : (calendarItem as any)
                                          .historicPenaltiesByYear?.length ? (
                                        (calendarItem as any).historicPenaltiesByYear.map(
                                          (
                                            h: {
                                              year: number;
                                              amount?: number | string | null;
                                              amountText?: string | null;
                                            },
                                            hi: number,
                                          ) => {
                                            const hasAmt =
                                              h.amount != null &&
                                              !(
                                                typeof h.amount === "string" &&
                                                h.amount.trim() === ""
                                              );
                                            return (
                                              <Typography
                                                key={hi}
                                                sx={{
                                                  fontSize: "12px",
                                                  fontWeight: 400,
                                                  color: "#262626",
                                                  fontFamily:
                                                    "Rethink Sans, sans-serif",
                                                  display: "block",
                                                  overflowWrap: "anywhere",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                {h.year}:{" "}
                                                {hasAmt
                                                  ? formatCurrencyAmountWhole(
                                                      h.amount,
                                                    )
                                                  : h.amountText || "—"}
                                              </Typography>
                                            );
                                          },
                                        )
                                      ) : (
                                        <Typography
                                          sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "#9e9e9e",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          —
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              );
                            },
                          )}
                            </Box>
                            </Box>
                            </Box>
                            </Box>
                          </>
                        )}
                      </Box>
                      </>
                      )}
                    </>
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            ))}

          <StyledAccordion
            expanded={expanded === "penaltiesAndTotalExposure"}
            onChange={handleAccordionChange("penaltiesAndTotalExposure")}
          >
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                alignItems: "center",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                  pr: 0.5,
                }}
              >
                <PenaltyIcon />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AccordionTitle
                    className="accordion-title"
                    sx={{ minWidth: 0 }}
                  >
                    Potential Penalties & Compliance Risks
                  </AccordionTitle>
                  {showRhcEnforcementBanner(selectedFacility as any, penaltyData) ? (
                    <Box
                      data-testid="rhc-enforcement-summary"
                      sx={{
                        color: "#334155",
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: 1.35,
                        mt: 0.25,
                        pr: 1,
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      {RHC_ENFORCEMENT_BANNER}
                    </Box>
                  ) : null}
                  {/* AB#10193 / SSOT-84: keep "Calculation Notes" visible in summary even when
                      accordion details collapse (exclusive accordion + RHC banner path). */}
                  <Box
                    data-testid="calculation-notes-summary"
                    sx={{
                      color: "#334155",
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: 1.35,
                      mt: 0.25,
                      pr: 1,
                    }}
                  >
                    Calculation Notes
                  </Box>
                </Box>
                {showPenaltiesLlmOnMainAccordion && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ flexShrink: 0 }}
                  >
                    <GlobalAdminLlmPromptButton
                      dialogTitle="Penalties & compliance risks — LLM prompt"
                      promptText={penaltiesLlmPrompt}
                      wallTimeSec={penaltiesLlmWallSec ?? undefined}
                    />
                  </Box>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                backgroundColor: "#ffffff",
                padding: "12px",
                position: "relative",
              }}
            >
              {/* ── Tab strip ── */}
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                {(
                  [
                    {
                      id: "penalties" as "penalties",
                      label: "Potential Penalties",
                      count: potentialPenaltyItems.length,
                    },
                    ...(recurringCostsTabCount > 0
                      ? [
                          {
                            id: "recurring" as "recurring",
                            label: "Recurring Costs",
                            count: recurringCostsTabCount,
                          },
                        ]
                      : []),
                    {
                      id: "contingent" as "contingent",
                      label: "Contingent Risks",
                      count:
                        penaltyData?.rollup?.contingentRisks?.length ?? 0,
                    },
                    ...(showAccreditationsTab
                      ? [
                          {
                            id: "accreditations" as "accreditations",
                            label: "Accreditations",
                            count: accredProgramsTotalForBadge ?? 0,
                          },
                        ]
                      : []),
                  ]
                ).map((tab) => {
                  const isActive = penaltySubTab === tab.id;
                  return (
                    <Box
                      key={tab.id}
                      onClick={() => setPenaltySubTab(tab.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "10px 16px",
                        cursor: "pointer",
                        borderBottom: isActive
                          ? "2px solid #065b70"
                          : "2px solid transparent",
                        marginBottom: "-1px",
                        color: isActive ? "#065b70" : "#6b7280",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "14px",
                        fontWeight: isActive ? 600 : 400,
                        userSelect: "none",
                        transition: "color 0.15s",
                        "&:hover": {
                          color: "#065b70",
                          backgroundColor: "#f8fdfe",
                        },
                      }}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <Box
                          sx={{
                            minWidth: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: isActive ? "#065b70" : "#e5e7eb",
                            color: isActive ? "#fff" : "#6b7280",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            fontWeight: 700,
                            px: "4px",
                          }}
                        >
                          {tab.count}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Potential Penalties table */}
              {penaltySubTab === "penalties" && (
                <Box
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: "12px",
                  }}
                >
                <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    size="small"
                    sx={{
                      width: "100%",
                      tableLayout: "auto",
                      "& .MuiTableCell-root": { verticalAlign: "top" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={penaltyTableHeaderSx}>
                          VIOLATION
                        </TableCell>
                        <TableCell sx={penaltyTableHeaderSx}>
                          DESCRIPTION
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          AMOUNT
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          RECURRING
                        </TableCell>
                        <TableCell sx={penaltyTableHeaderSx}>
                          Calculation Notes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {potentialPenaltyItems.map(
                        (item: any, idx: number) => {
                          const amount =
                            item?.workedExample?.result?.amount ?? 0;
                          const showAmount = Number(amount) > 0;
                          const recurring = detectRecurring(item);
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                "&:last-child td": { borderBottom: "none" },
                                "&:hover": { backgroundColor: "#f8fdfe" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 600,
                                  minWidth: 140,
                                  color: "#065b70",
                                }}
                              >
                                {item.name}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  minWidth: 200,
                                  color: "#374151",
                                }}
                              >
                                <PenaltyRuleOrQuoteDescription
                                  ruleOrQuote={item.ruleOrQuote}
                                  regulationCites={item.regulationCites}
                                  citation={item.citation}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color: "#065b70",
                                }}
                              >
                                {showAmount
                                  ? formatCurrencyAmount(amount)
                                  : "—"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {recurring ? (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      px: "10px",
                                      py: "3px",
                                      borderRadius: "12px",
                                      border: "1px solid #f59e0b",
                                      backgroundColor: "#fffbeb",
                                      color: "#92400e",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Yes
                                  </Box>
                                ) : (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      px: "10px",
                                      py: "3px",
                                      borderRadius: "12px",
                                      border: "1px solid #d1d5db",
                                      backgroundColor: "#f9fafb",
                                      color: "#6b7280",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    No
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontStyle: "italic",
                                  color: "#6b7280",
                                  minWidth: 160,
                                }}
                              >
                                {penaltyItemNotes(item)
                                  ? stripMarkdownLight(penaltyItemNotes(item))
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                    {potentialPenaltyItems.length > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              py: 1.5,
                              px: 2,
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#6b7280",
                              fontFamily: "Rethink Sans, sans-serif",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              verticalAlign: "top",
                            }}
                          >
                            SUBTOTAL — POTENTIAL PENALTIES
                          </TableCell>
                          <TableCell
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              py: 1.5,
                              px: "12px",
                              fontSize: "16px",
                              fontWeight: 700,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                              whiteSpace: "nowrap",
                              verticalAlign: "top",
                              textAlign: "left",
                            }}
                          >
                            {formatCurrencyAmount(
                              potentialPenaltyItems.reduce(
                                (sum: number, item: any) =>
                                  sum +
                                  (Number(item?.workedExample?.result?.amount) ||
                                    0),
                                0,
                              ),
                            )}
                          </TableCell>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              verticalAlign: "top",
                            }}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </Box>
                </Box>
                </Box>
              )}

              {/* ── Recurring Costs table ── */}
              {penaltySubTab === "recurring" && (
                <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden", mt: "12px" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    size="small"
                    sx={{
                      width: "100%",
                      tableLayout: "auto",
                      "& .MuiTableCell-root": { verticalAlign: "top" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={penaltyTableHeaderSx}>FEE</TableCell>
                        <TableCell sx={penaltyTableHeaderSx}>
                          DESCRIPTION
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ANNUAL AMOUNT
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          FREQUENCY
                        </TableCell>
                        <TableCell sx={penaltyTableHeaderSx}>
                          AUTHORITY / REFERENCE
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(penaltyData?.rollup?.recurringAnnualCosts ?? [])
                        .filter((c: any) => c != null)
                        .map((cost: any, idx: number) => {
                          const hasValidAmount =
                            cost?.amount?.amount !== null &&
                            cost?.amount?.amount !== undefined;
                          const displayAmount = hasValidAmount
                            ? formatCurrencyAmount(cost.amount.amount)
                            : cost?.amount?.notes ?? "Fee varies";
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                "&:last-child td": { borderBottom: "none" },
                                "&:hover": { backgroundColor: "#f8fdfe" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 600,
                                  minWidth: 160,
                                  color: "#065b70",
                                }}
                              >
                                {cost.label}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  minWidth: 180,
                                  color: "#374151",
                                }}
                              >
                                {hasValidAmount && cost?.amount?.notes
                                  ? cost.amount.notes
                                  : cost?.description ?? "—"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color: hasValidAmount ? "#065b70" : "#9a3412",
                                  fontStyle: hasValidAmount ? "normal" : "italic",
                                }}
                              >
                                {displayAmount}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: "10px",
                                    py: "3px",
                                    borderRadius: "12px",
                                    border: "1px solid #d1d5db",
                                    backgroundColor: "#f9fafb",
                                    color: "#374151",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  Annual
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  color: "#6b7280",
                                }}
                              >
                                {cost?.citation?.publisher ??
                                  cost?.citation?.title ??
                                  cost?.reference ??
                                  "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                    {(penaltyData?.rollup?.recurringAnnualCosts?.filter(
                      (c: any) => c != null,
                    ).length ?? 0) > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              py: 1.5,
                              px: 2,
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#6b7280",
                              fontFamily: "Rethink Sans, sans-serif",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              verticalAlign: "top",
                            }}
                          >
                            SUBTOTAL — RECURRING ANNUAL COSTS
                          </TableCell>
                          <TableCell
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              py: 1.5,
                              px: "12px",
                              fontSize: "16px",
                              fontWeight: 700,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                              whiteSpace: "nowrap",
                              verticalAlign: "top",
                              textAlign: "left",
                            }}
                          >
                            {formatCurrencyAmount(
                              (penaltyData?.rollup?.recurringAnnualCosts ?? [])
                                .filter((c: any) => c != null)
                                .reduce(
                                  (sum: number, cost: any) =>
                                    sum + (Number(cost?.amount?.amount) || 0),
                                  0,
                                ),
                            )}
                          </TableCell>
                          <TableCell
                            colSpan={2}
                            sx={{
                              borderTop: "2px solid #e5e7eb",
                              backgroundColor: "#f8fafc",
                              verticalAlign: "top",
                            }}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </Box>
                </Box>
              )}

              {/* ── Contingent Risks table ── */}
              {penaltySubTab === "contingent" && (
                <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden", mt: "12px" }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    size="small"
                    sx={{ width: "100%", tableLayout: "auto" }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={penaltyTableHeaderSx}>RISK</TableCell>
                        <TableCell sx={penaltyTableHeaderSx}>
                          TRIGGER CONDITION
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          MAX EXPOSURE
                        </TableCell>
                        <TableCell
                          sx={{
                            ...penaltyTableHeaderSx,
                            whiteSpace: "nowrap",
                          }}
                        >
                          RISK LEVEL
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(penaltyData?.rollup?.contingentRisks ?? []).map(
                        (risk: any, idx: number) => {
                          const info = extractContingentRiskInfo(risk);
                          const riskColor =
                            info.riskLevel === "High"
                              ? "#dc2626"
                              : info.riskLevel === "Medium"
                                ? "#d97706"
                                : "#16a34a";
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                "&:last-child td": { borderBottom: "none" },
                                "&:hover": { backgroundColor: "#f8fdfe" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 600,
                                  minWidth: 160,
                                  color: "#065b70",
                                }}
                              >
                                {info.name}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  minWidth: 220,
                                  color: "#374151",
                                }}
                              >
                                {info.trigger}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  fontWeight: 600,
                                  color: "#065b70",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {info.exposure}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...penaltyTableCellSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {info.riskLevel && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: riskColor,
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: riskColor,
                                        flexShrink: 0,
                                      }}
                                    />
                                    {info.riskLevel}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                </Box>
                </Box>
              )}
              {/* ── Accreditations tab ── */}
              {penaltySubTab === "accreditations" && (
                <Box sx={{ mt: "12px" }}>
                  {accreditationsLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={28} sx={{ color: "#0a829d" }} />
                    </Box>
                  )}
                  {accreditationsError && !accreditationsLoading && (
                    <Typography variant="body2" color="error" sx={{ py: 2 }}>
                      {accreditationsError}
                    </Typography>
                  )}
                  {!accreditationsLoading && !accreditationsError && accreditingBodySlugs.length === 0 && (
                    <Typography variant="body2" sx={{ color: "#6b7280", py: 2 }}>
                      No accrediting bodies selected for this facility.
                    </Typography>
                  )}
                  {!accreditationsLoading && accreditationsData && Object.keys(accreditationsData).length === 0 && (
                    <Typography variant="body2" sx={{ color: "#6b7280", py: 2 }}>
                      No accreditation program data found for the selected bodies.
                    </Typography>
                  )}
                  {!accreditationsLoading &&
                    accreditationsData &&
                    Object.entries(accreditationsData).map(([slug, body]: [string, BodyProgramData]) => (
                      <Box key={slug} sx={{ mb: 3 }}>
                        <Typography
                          sx={{
                            fontFamily: "Rethink Sans, sans-serif",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#065b70",
                            mb: 1,
                          }}
                        >
                          {body.fullName ?? body.bodyAcronym ?? slug}
                          {body.bodyAcronym && body.fullName && body.bodyAcronym !== body.fullName && (
                            <Box component="span" sx={{ fontWeight: 400, color: "#6b7280", ml: 1, fontSize: "13px" }}>
                              ({body.bodyAcronym})
                            </Box>
                          )}
                        </Typography>
                        {(!body.programs || body.programs.length === 0) ? (
                          <Typography variant="body2" sx={{ color: "#9ca3af", fontSize: "13px" }}>
                            No accreditation programs are catalogued for this facility type under
                            this body (Medicare Conditions of Participation may still apply).
                          </Typography>
                        ) : (
                          <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden" }}>
                            <Box sx={{ overflowX: "auto" }}>
                              <Table size="small" sx={{ width: "100%", tableLayout: "auto" }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={penaltyTableHeaderSx}>PROGRAM</TableCell>
                                    <TableCell sx={penaltyTableHeaderSx}>SECTION</TableCell>
                                    <TableCell sx={penaltyTableHeaderSx}>CITATION</TableCell>
                                    <TableCell sx={{ ...penaltyTableHeaderSx, whiteSpace: "nowrap" }}>
                                      FREQUENCY / SCHEDULE
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {body.programs.map((prog, pi) =>
                                    (prog.sections ?? []).map((sec, si) => (
                                      <TableRow
                                        key={`${pi}-${si}`}
                                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                                      >
                                        {si === 0 && (
                                          <TableCell
                                            rowSpan={prog.sections?.length ?? 1}
                                            sx={{
                                              fontFamily: "Rethink Sans, sans-serif",
                                              fontSize: "13px",
                                              fontWeight: 600,
                                              color: "#111827",
                                              verticalAlign: "top",
                                              py: 1,
                                              px: "12px",
                                              borderRight: "1px solid #e5e7eb",
                                            }}
                                          >
                                            {prog.program ?? prog.programSlug ?? "—"}
                                          </TableCell>
                                        )}
                                        <TableCell
                                          sx={{
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontSize: "13px",
                                            color: "#374151",
                                            verticalAlign: "top",
                                            py: 1,
                                            px: "12px",
                                          }}
                                        >
                                          {sec.title ?? sec.id ?? "—"}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontSize: "12px",
                                            color: "#6b7280",
                                            verticalAlign: "top",
                                            py: 1,
                                            px: "12px",
                                          }}
                                        >
                                          {typeof sec.citation === "object" && sec.citation ? (
                                            sec.citation.url ? (
                                              <a
                                                href={sec.citation.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#0a829d", textDecoration: "none" }}
                                              >
                                                {sec.citation.title ?? sec.citation.url}
                                              </a>
                                            ) : (
                                              sec.citation.title ?? "—"
                                            )
                                          ) : (
                                            String(sec.citation ?? "—")
                                          )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontSize: "13px",
                                            color: "#374151",
                                            verticalAlign: "top",
                                            py: 1,
                                            px: "12px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {sec.frequency ?? sec.scheduleDescription ?? "—"}
                                        </TableCell>
                                      </TableRow>
                                    )),
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                </Box>
              )}
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion
            expanded={expanded === "calculationNotes"}
            onChange={handleAccordionChange("calculationNotes")}
            data-testid="calculation-notes-accordion"
          >
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                alignItems: "center",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AccordionTitle className="accordion-title" sx={{ minWidth: 0 }}>
                    Calculation Notes
                  </AccordionTitle>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                backgroundColor: "#ffffff",
                padding: "12px",
              }}
            >
              {potentialPenaltyItems.length === 0 ? (
                <Box
                  sx={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontStyle: "italic",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  No calculation notes for this facility.
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {(() => {
                    const rows = potentialPenaltyItems.map((item: any, idx: number) => {
                      const raw = penaltyItemNotes(item);
                      const shared = isSharedRhcEnforcementNote(raw);
                      const rendered = raw && !shared ? stripMarkdownLight(raw) : "";
                      return {
                        key: idx,
                        name: item?.name || `Penalty ${idx + 1}`,
                        raw,
                        shared,
                        rendered,
                      };
                    });
                    const sharedNote =
                      rows.find((r) => r.shared && r.raw)?.raw
                      ?? (showRhcEnforcementBanner(selectedFacility as any, penaltyData)
                        ? RHC_ENFORCEMENT_BANNER
                        : "");
                    const sharedRendered = sharedNote
                      ? stripMarkdownLight(sharedNote)
                      : "";
                    return (
                      <>
                        {sharedRendered ? (
                          <Box
                            data-testid="calculation-notes-shared-rhc"
                            sx={{
                              borderBottom: "1px solid #e5e7eb",
                              pb: 1,
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: 600,
                                color: "#065b70",
                                fontSize: "13px",
                                mb: 0.5,
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              RHC enforcement (applies to condition-level items)
                            </Box>
                            <Box
                              sx={{
                                color: "#374151",
                                fontSize: "13px",
                                whiteSpace: "pre-wrap",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {sharedRendered}
                            </Box>
                          </Box>
                        ) : null}
                        {rows.map((row) => (
                          <Box
                            key={row.key}
                            sx={{
                              borderBottom: "1px solid #e5e7eb",
                              pb: 1,
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: 600,
                                color: "#065b70",
                                fontSize: "13px",
                                mb: 0.5,
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {row.name}
                            </Box>
                            <Box
                              sx={{
                                color: "#374151",
                                fontSize: "13px",
                                whiteSpace: "pre-wrap",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {row.shared ? "—" : row.rendered || "—"}
                            </Box>
                          </Box>
                        ))}
                      </>
                    );
                  })()}
                </Box>
              )}
            </AccordionDetails>
          </StyledAccordion>

          {!hideCybersecurityComplianceRisk && (
          <StyledAccordion
            expanded={expanded === "cybersecurityComplianceRisk"}
            onChange={handleAccordionChange("cybersecurityComplianceRisk")}
          >
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                alignItems: "center",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: "20px", height: "20px" }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                      stroke="#065b70"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 6V10"
                      stroke="#065b70"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 14H10.01"
                      stroke="#065b70"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <AccordionTitle className="accordion-title">
                  Cybersecurity Compliance Risk
                </AccordionTitle>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                backgroundColor: "#ffffff",
                padding: "12px",
                position: "relative",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {!cyberFacilityType || !cyberCountryCode ? (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Select a facility and ensure your provider type is set to
                    see cybersecurity penalty exposure for your facility type.
                  </Typography>
                ) : cyberLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1,
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={24} sx={{ color: "#065b70" }} />
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Loading cybersecurity penalty data...
                      </Typography>
                    </Box>
                    {cyberLoadingHint && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontFamily: "Rethink Sans, sans-serif",
                          fontStyle: "italic",
                        }}
                      >
                        Taking a while? Ensure REACT_APP_API_BASEURL points to
                        your backend (e.g. http://localhost:8000) and the
                        server allows CORS from this origin.
                      </Typography>
                    )}
                  </Box>
                ) : cyberError ? (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#9a3412",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    {cyberError}
                  </Typography>
                ) : !cyberHasPenaltyData ? null : (
                  <>
                    <TableContainer
                      sx={{
                        border: "1px solid #0a829d",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                fontFamily: "Rethink Sans, sans-serif",
                                backgroundColor: "#0a829d",
                                fontSize: "11px",
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #0a6a80",
                              }}
                            >
                              Source
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                fontFamily: "Rethink Sans, sans-serif",
                                backgroundColor: "#0a829d",
                                fontSize: "11px",
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #0a6a80",
                              }}
                            >
                              Regulation
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                fontFamily: "Rethink Sans, sans-serif",
                                backgroundColor: "#0a829d",
                                fontSize: "11px",
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #0a6a80",
                              }}
                            >
                              Penalty
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                fontFamily: "Rethink Sans, sans-serif",
                                backgroundColor: "#0a829d",
                                fontSize: "11px",
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #0a6a80",
                              }}
                            >
                              Violation Type
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                fontFamily: "Rethink Sans, sans-serif",
                                backgroundColor: "#0a829d",
                                fontSize: "11px",
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                borderBottom: "1px solid #0a6a80",
                              }}
                            >
                              Projected
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleCyberExposureVectors.map(
                            (vec, idx) => {
                              const ps = vec.penalty_structure ?? {};
                              const hasViol =
                                ps.per_violation_min != null ||
                                ps.per_violation_max != null;
                              const hasDay =
                                ps.per_day_min != null ||
                                ps.per_day_max != null;
                              const showProjected = hasViol || hasDay;
                              const projected = computeCyberProjected(vec);
                              const penaltyParts: string[] = [];
                              if (hasViol)
                                penaltyParts.push(
                                  `${formatCurrencyAmount(ps.per_violation_min ?? 0)}–${formatCurrencyAmount(ps.per_violation_max ?? 0)} per violation`,
                                );
                              if (hasDay)
                                penaltyParts.push(
                                  `${formatCurrencyAmount(ps.per_day_min ?? 0)}–${formatCurrencyAmount(ps.per_day_max ?? 0)} per day`,
                                );
                              if (ps.annual_cap != null)
                                penaltyParts.push(
                                  `Annual cap: ${formatCurrencyAmount(ps.annual_cap)}`,
                                );
                              const penaltyText =
                                penaltyParts.length > 0
                                  ? penaltyParts.join(" • ")
                                  : "N/A";
                              const cfrUrl = buildCfrLink(vec.cfr_reference);
                              const regimeLabel = (vec.regulatory_regime ?? "Other").replace(/_/g, " ");
                              return (
                                <TableRow key={idx}>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {regimeLabel}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {cfrUrl ? (
                                      <a
                                        href={cfrUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#2563eb",
                                          textDecoration: "none",
                                        }}
                                      >
                                        {vec.cfr_reference ?? regimeLabel}{" "}
                                        <span aria-hidden>↗</span>
                                      </a>
                                    ) : (
                                      vec.cfr_reference ?? regimeLabel
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {penaltyText}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {vec.violation_category ?? "N/A"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: "14px",
                                      color: cyberHighlightedRows.has(idx) ? "#b91c1c" : "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      transition: "color 0.3s ease",
                                    }}
                                  >
                                    {showProjected
                                      ? projected.min === projected.max
                                        ? formatCurrencyAmount(projected.min)
                                        : `${formatCurrencyAmount(projected.min)}–${formatCurrencyAmount(projected.max)}`
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                              );
                            },
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box
                      sx={{
                        backgroundColor: "#f8fdfe",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #cff7ff",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                          mb: 1,
                        }}
                      >
                        Project your exposure
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <TextField
                            type="number"
                            size="small"
                            label="Number of violations"
                            inputProps={{ min: 0, step: 1 }}
                            value={cyberViolations}
                            onChange={(e) =>
                              setCyberViolations(
                                Math.max(0, parseInt(e.target.value, 10) || 0),
                              )
                            }
                            sx={{
                              width: "140px",
                              "& .MuiInputBase-root": {
                                fontFamily: "Rethink Sans, sans-serif",
                              },
                            }}
                          />
                          <TextField
                            type="number"
                            size="small"
                            label="Days of non-compliance"
                            inputProps={{ min: 0, step: 1 }}
                            value={cyberDays}
                            onChange={(e) =>
                              setCyberDays(
                                Math.max(0, parseInt(e.target.value, 10) || 0),
                              )
                            }
                            sx={{
                              width: "180px",
                              "& .MuiInputBase-root": {
                                fontFamily: "Rethink Sans, sans-serif",
                              },
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCyberCalculate}
                            sx={{
                              backgroundColor: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Calculate
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            marginLeft: "auto",
                          }}
                        >
                          <TextField
                            type="date"
                            size="small"
                            label="Reference date"
                            value={cyberRefDate}
                            onChange={(e) =>
                              setCyberRefDate(e.target.value || cyberRefDate)
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              width: "160px",
                              "& .MuiInputBase-root": {
                                fontFamily: "Rethink Sans, sans-serif",
                              },
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCyberRefresh}
                            sx={{
                              borderColor: "#065b70",
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Refresh
                          </Button>
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#6b7280",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Enter projected values to estimate potential
                        penalties.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </StyledAccordion>
          )}

          {showHistoricPenaltiesSection && (
            <HistoricPenaltiesPanel
              selectedFacility={
                selectedFacility as unknown as Record<
                  string,
                  unknown
                > | null
              }
              onSomTagClick={openSomSectionDialog}
              pdcInfoTooltip={<CmsNursingHomePdcInfoTooltip />}
            />
          )}
        </ContentContainer>

        <SomSectionDetailDialog
          open={somSectionDialogOpen}
          tag={somSectionDialogTag}
          countryCode={cyberCountryCode}
          industry="healthcare"
          onClose={closeSomSectionDialog}
        />

        <Dialog
          open={historicPenaltyDialogEvent !== null}
          onClose={() => setHistoricPenaltyDialogEvent(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: "18px",
              fontWeight: 600,
              color: "#262626",
              py: 2.25,
              px: 3,
              pb: 1.5,
              borderBottom: "1px solid #e5f5f8",
            }}
          >
            Historic enforcement action
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {historicPenaltyDialogEvent && (
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom: "1px solid #e5f5f8",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#065b70",
                      lineHeight: 1.35,
                    }}
                  >
                    {historicPenaltyDialogEvent.title}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "#f8fdfe",
                    border: "1px solid #e5f5f8",
                    p: 2,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      columnGap: 2.5,
                      rowGap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mb: 0.5,
                        }}
                      >
                        Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "22px",
                          fontWeight: 700,
                          color: "#065b70",
                          lineHeight: 1.2,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatCurrencyAmountWhole(
                          historicPenaltyDialogEvent.amount,
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mb: 0.5,
                        }}
                      >
                        Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#202124",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {historicPenaltyDialogEvent.actionDate ?? "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mb: 0.5,
                        }}
                      >
                        Ref
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#202124" }}>
                        {historicPenaltyDialogEvent.reference ??
                          historicPenaltyDialogEvent.caseNumber ??
                          "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mb: 0.5,
                        }}
                      >
                        Jurisdiction
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#202124" }}>
                        {historicPenaltyDialogEvent.jurisdiction}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#6b7280",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 1,
                      }}
                    >
                      Codes cited
                    </Typography>
                    <HistoricPenaltyCodeChips
                      somTags={historicPenaltyDialogEvent.somTags}
                      violationCode={historicPenaltyDialogEvent.violationCode}
                    />
                  </Box>

                  <Divider sx={{ borderColor: "#e5f5f8" }} />

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#6b7280",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 0.5,
                      }}
                    >
                      Authority
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#202124" }}>
                      {historicPenaltyDialogEvent.issuingAuthority}
                    </Typography>
                    {!isSourceRedundantWithAuthority(
                      historicPenaltyDialogEvent.source,
                      historicPenaltyDialogEvent.issuingAuthority,
                    ) ? (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#6b7280",
                          mt: 0.75,
                        }}
                      >
                        Source: {historicPenaltyDialogEvent.source}
                      </Typography>
                    ) : null}
                    {historicPenaltyDialogEvent.regulator ? (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#6b7280",
                          mt: 0.75,
                        }}
                      >
                        Regulator: {historicPenaltyDialogEvent.regulator}
                      </Typography>
                    ) : null}
                    {(historicPenaltyDialogEvent.caseType ||
                      historicPenaltyDialogEvent.actionType ||
                      historicPenaltyDialogEvent.affectedIndividuals != null) && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 1,
                        }}
                      >
                        {historicPenaltyDialogEvent.caseType ? (
                          <Chip
                            label={historicPenaltyDialogEvent.caseType}
                            size="small"
                            sx={{ fontFamily: "Rethink Sans, sans-serif" }}
                          />
                        ) : null}
                        {historicPenaltyDialogEvent.actionType ? (
                          <Chip
                            label={historicPenaltyDialogEvent.actionType}
                            size="small"
                            sx={{ fontFamily: "Rethink Sans, sans-serif" }}
                          />
                        ) : null}
                        {historicPenaltyDialogEvent.affectedIndividuals != null ? (
                          <Chip
                            label={`${historicPenaltyDialogEvent.affectedIndividuals} affected`}
                            size="small"
                            sx={{ fontFamily: "Rethink Sans, sans-serif" }}
                          />
                        ) : null}
                      </Box>
                    )}
                    {(historicPenaltyDialogEvent.sourceUrl ||
                      historicPenaltyDialogEvent.documentUrl) && (
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                        {historicPenaltyDialogEvent.sourceUrl ? (
                          <Link
                            href={historicPenaltyDialogEvent.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "13px" }}
                          >
                            View source
                          </Link>
                        ) : null}
                        {historicPenaltyDialogEvent.documentUrl ? (
                          <Link
                            href={historicPenaltyDialogEvent.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "13px" }}
                          >
                            View document
                          </Link>
                        ) : null}
                      </Box>
                    )}
                  </Box>

                  {historicPenaltyDialogEvent.description ? (
                    <Box>
                      {(() => {
                        const parsed = parsePenaltyDetailDescription(
                          historicPenaltyDialogEvent.description ?? "",
                        );
                        if (parsed) {
                          return (
                            <>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  mb: 0.5,
                                }}
                              >
                                {parsed.label}
                              </Typography>
                              <Chip
                                label={parsed.value}
                                size="small"
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  height: 30,
                                  backgroundColor: "#fff",
                                  border: "1px solid #c5e4eb",
                                  color: "#065b70",
                                }}
                              />
                            </>
                          );
                        }
                        return (
                          <>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#6b7280",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                mb: 0.5,
                              }}
                            >
                              Details
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#202124",
                                lineHeight: 1.5,
                              }}
                            >
                              {historicPenaltyDialogEvent.description}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid #e5f5f8",
              justifyContent: "flex-end",
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setHistoricPenaltyDialogEvent(null)}
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: "#065b70",
                borderColor: "#065b70",
                textTransform: "none",
                letterSpacing: "0.02em",
                px: 2.5,
                py: 0.75,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "#054a5c",
                  backgroundColor: "rgba(6, 91, 112, 0.08)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {hasActiveCitation && selectedCitation && (
          <CitationPanelContainer>
            <CitationHeader>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "white",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                Requirement Details
              </Typography>
              <CloseButton onClick={closeCitationPanel}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M9 3L3 9M3 3L9 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
            </CitationHeader>
            <Box
              sx={{
                padding: "16px",
                borderBottom: "1px solid #e5f5f8",
                backgroundColor: "#f8fdfe",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#065b70",
                  marginBottom: "8px",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                {selectedCitation.requirement}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#737373",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                Source: {selectedCitation.publisher || "Government Authority"}
              </Typography>
            </Box>
            <Box
              sx={{
                height: "calc(100% - 120px)",
                overflow: "hidden",
              }}
            >
              <iframe
                src={selectedCitation.url}
                title={selectedCitation.requirement}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </Box>
          </CitationPanelContainer>
        )}
      </MainContent>
    </PenaltyContainer>
  );
};

export default Penalty;
